import {
  Component,
  OnInit,
  AfterViewInit,
  OnDestroy,
  HostListener,
  ElementRef,
  ViewChild,
  inject,
  signal,
  effect,
  untracked,
  forwardRef,
  computed,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { v4 as uuidv4 } from 'uuid';

import { AuthStore } from '@rapider/authentication';
import {
  ProjectDataService,
  isApplicationVisibleToUser,
} from '@rapider/project-data';
import { BrowserStorageManagementService } from '@rapider/services';
import { DefinitionControllerService, FileManagementControllerService, CommentControllerService, Comment } from '@rapider/sdk'; // Imported SDK Services
import { environment } from '@rapider/environments';
import { firstValueFrom, Subscription } from 'rxjs';

import { HttpClient } from '@angular/common/http';
import { FormsModule } from '@angular/forms';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzSplitterComponent, NzSplitterModule } from 'ng-zorro-antd/splitter';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzDropDownModule } from 'ng-zorro-antd/dropdown';
import { NzMenuModule } from 'ng-zorro-antd/menu';
import { NzTimelineModule } from 'ng-zorro-antd/timeline';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzSwitchModule } from 'ng-zorro-antd/switch';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';
import { Store } from '@ngrx/store';

import { AiChatActions } from '@rapider/ai-chat';
import { RapiderMarkdownEditorComponent } from '@rapider/markdown-editor';
import { MarkdownPreviewComponent } from '@rapider/markdown-preview';
import { NotificationService } from '@rapider/services';

import { DynamicPageRendererComponent } from '../dynamic-page-renderer/dynamic-page-renderer.component';
import { HtmlDefinitionPreviewComponent } from '../html-definition-preview/html-definition-preview.component';
import { HtmlUrlPreviewComponent } from '../html-url-preview/html-url-preview.component';
import { normalizeAppThemeKey } from '../html-preview/html-preview.component';
import {
  AppConfig,
  PageConfig,
  DYNAMIC_APP_VIEWER_CONFIG,
  buildPageTemplateFilePath,
  DefinitionFileNames,
} from '../dynamic-page-renderer/dynamic-page-renderer.config';
import { ApplicationArtifactsComponent } from '../application-artifacts/application-artifacts.component';
import { E2eTestRunnerService } from './e2e-test-runner.service';
import { TaskPipelineGuideModalComponent } from '../task-pipeline-guide-modal/task-pipeline-guide-modal.component';
import { isTrustedAppResourceUrl } from '../security/sandbox-security.util';

// Global cache to prevent double-fetching when Angular recreates the component during a redirect
const manifestCache: { url: string | null; data: any | null } = {
  url: null,
  data: null,
};

/**
 * @interface UnifiedTaskItem
 * @description Represents a unified task item merging both Bug Reports (Comments)
 * and Feature Requests into a standardized model for display and management.
 */
export interface UnifiedTaskItem {
  /** Sequential human-readable task identifier (e.g., TSK-101) */
  id: string;
  /** Sequential bubble number matching canvas marker */
  bubbleNumber?: number;
  /** Raw entity ID in backend database */
  rawId: string;
  /** Title or summary of the task */
  title: string;
  /** Full markdown content / description */
  description: string;
  /** Task type categorization ('Bug' or 'Feature') */
  type: 'Bug' | 'Feature';
  /** Original sub-type if feature request */
  rawType?: string;
  /** Normalized lifecycle status */
  status: string;
  /** Whether the task has been approved by admin or app owner */
  isApproved?: boolean | null;
  /** Author or reporter display name */
  createdBy: string;
  /** Author or reporter ID for ownership and resolution permission checks */
  createdById?: string;
  /** Optional author avatar image URL */
  authorAvatarUrl?: string;
  /** Creation timestamp */
  createdAt: string | Date;
  /** Attached DOM element selector or ID */
  elementSelector?: string;
  /** Attached DOM element ID */
  elementId?: string;
  /** Attached DOM element HTML tag name */
  tag?: string;
  /** Optional screen coordinate metadata when a bubble is dropped */
  bubbleMetadata?: {
    x: number;
    y: number;
    pageId?: string;
  };
  /** Screen coordinates alias for template usage */
  coordinates?: {
    x: number;
    y: number;
    pageId?: string;
  };
  /** Page route, ID, or key where this task was created */
  pageId?: string;
  /** Total number of replies to this task / comment */
  replyCount?: number;
  /** Names of up to 3 people who recently replied to this task */
  lastReplierNames?: string[];
  /** Reference to original underlying entity */
  rawItem: any;
}

/**
 * Determines whether a calculated bubble coordinate falls within the visible boundaries
 * of the canvas container, allowing for a slight margin to avoid premature clipping.
 *
 * @param top The vertical offset of the bubble relative to the canvas.
 * @param left The horizontal offset of the bubble relative to the canvas.
 * @param canvasWidth The width of the visible canvas rectangle.
 * @param canvasHeight The height of the visible canvas rectangle.
 * @param margin Allowed margin in pixels outside the container bounds (default: 20).
 * @returns True if the bubble coordinate is within the visible boundaries, false otherwise.
 */
export function isCoordinateWithinCanvasBounds(
  top: number,
  left: number,
  canvasWidth: number,
  canvasHeight: number,
  margin = 20,
): boolean {
  if (canvasWidth <= 0 || canvasHeight <= 0) {
    return true;
  }
  return (
    top >= -margin &&
    top <= canvasHeight + margin &&
    left >= -margin &&
    left <= canvasWidth + margin
  );
}

/**
 * Evaluates the next filter value when toggling a filter option.
 * If the clicked option is already active, resets to the default fallback value.
 *
 * @param current - The current active filter value.
 * @param selected - The newly selected filter value.
 * @param fallback - The fallback default value (default: 'ALL').
 * @returns The next filter value to set.
 */
export function toggleFilterValue(
  current: string,
  selected: string,
  fallback = 'ALL',
): string {
  if (current === selected && selected !== fallback) {
    return fallback;
  }
  return selected;
}

/**
 * @class DynamicAppViewerComponent
 * @description A host wrapper component designed to fetch an overall Application Configuration JSON
 * (currently sourced from Definitions DB), extract the requested page configuration,
 * fetch that page's specific HTML template via its UUID using the SDK, update the configuration, and mount the `DynamicPageRendererComponent`.
 */
@Component({
  selector: 'rapider-dynamic-app-viewer',
  standalone: true,
  imports: [
    NzSplitterModule,
    NzModalModule,
    NzButtonModule,
    NzInputModule,
    CommonModule,
    FormsModule,
    NzSelectModule,
    RapiderMarkdownEditorComponent,
    MarkdownPreviewComponent,
    forwardRef(() => DynamicPageRendererComponent),
    HtmlDefinitionPreviewComponent,
    HtmlUrlPreviewComponent,
    ApplicationArtifactsComponent,
    NzDropDownModule,
    NzMenuModule,
    NzTimelineModule,
    NzTagModule,
    NzSwitchModule,
    NzTooltipModule,
    TaskPipelineGuideModalComponent,
  ],
  templateUrl: './dynamic-app-viewer.component.html',
  host: {
    class: 'block w-full h-full',
  },
  styles: [
    `
      .selection-pin {
        animation: pulse-pin 1.5s infinite;
      }
      @keyframes pulse-pin {
        0%, 100% { transform: translate(-50%, -50%) scale(1); }
        50% { transform: translate(-50%, -50%) scale(1.15); }
      }
      .comment-hover-target {
        outline: 2px dashed #ef4444 !important;
        outline-offset: -2px;
        cursor: crosshair !important;
      }
      ::ng-deep .comment-hover-target {
        outline: 2px dashed #ef4444 !important;
        outline-offset: -2px;
        cursor: crosshair !important;
      }
      @keyframes task-card-single-shake {
        0%, 100% {
          transform: translateX(0);
        }
        15% {
          transform: translateX(-4px);
        }
        30% {
          transform: translateX(4px);
        }
        50% {
          transform: translateX(-3px);
        }
        70% {
          transform: translateX(2px);
        }
        85% {
          transform: translateX(-1px);
        }
      }
      @keyframes bubble-delayed-shake {
        0%, 65%, 100% {
          transform: scale(1.18) translateX(0) rotate(0deg);
        }
        68% {
          transform: scale(1.18) translateX(-3px) rotate(-1.5deg);
        }
        72% {
          transform: scale(1.18) translateX(3px) rotate(1.5deg);
        }
        76% {
          transform: scale(1.18) translateX(-3px) rotate(-1.2deg);
        }
        80% {
          transform: scale(1.18) translateX(3px) rotate(1.2deg);
        }
        84% {
          transform: scale(1.18) translateX(-1.5px) rotate(-0.6deg);
        }
        88% {
          transform: scale(1.18) translateX(1.5px) rotate(0.6deg);
        }
        92% {
          transform: scale(1.18) translateX(0) rotate(0deg);
        }
      }
      .task-card-active {
        border-color: #f59e0b !important;
        background: linear-gradient(135deg, #fffbeb 0%, #ffffff 100%) !important;
        box-shadow: 0 0 0 2px #f59e0b, 0 6px 16px -2px rgba(245, 158, 11, 0.25) !important;
        animation: task-card-single-shake 0.5s ease-in-out 1;
      }
      .comment-bubble {
        margin-top: -32px;
        transform-origin: bottom left;
      }
      .comment-bubble-active {
        box-shadow: 0 0 0 3px #ffffff, 0 0 0 6px #f59e0b, 0 8px 16px rgba(245, 158, 11, 0.45) !important;
        transform: scale(1.18);
        animation: bubble-delayed-shake 3.5s ease-in-out infinite;
        will-change: transform;
        z-index: 50 !important;
      }
      .task-card-markdown ::ng-deep .semantic-markdown {
        font-size: 11px !important;
        line-height: 1.5 !important;
        color: #4b5563 !important;
      }
      .task-card-markdown ::ng-deep .semantic-markdown p {
        font-size: 11px !important;
        line-height: 1.5 !important;
        margin-bottom: 0.35rem !important;
      }
      .task-card-markdown ::ng-deep .semantic-markdown p:last-child {
        margin-bottom: 0 !important;
      }
      .task-card-markdown ::ng-deep .semantic-markdown h1,
      .task-card-markdown ::ng-deep .semantic-markdown h2,
      .task-card-markdown ::ng-deep .semantic-markdown h3,
      .task-card-markdown ::ng-deep .semantic-markdown h4,
      .task-card-markdown ::ng-deep .semantic-markdown h5,
      .task-card-markdown ::ng-deep .semantic-markdown h6 {
        font-size: 11px !important;
        font-weight: 700 !important;
        margin-top: 0.35rem !important;
        margin-bottom: 0.2rem !important;
        line-height: 1.3 !important;
      }
      .task-card-markdown ::ng-deep .semantic-markdown ul,
      .task-card-markdown ::ng-deep .semantic-markdown ol {
        margin-left: 1rem !important;
        margin-bottom: 0.35rem !important;
        font-size: 11px !important;
      }
      .task-card-markdown ::ng-deep .semantic-markdown li {
        margin-bottom: 0.1rem !important;
        font-size: 11px !important;
      }
      .task-card-markdown ::ng-deep .semantic-markdown code {
        font-size: 10px !important;
        padding: 0.1rem 0.25rem !important;
      }
      .task-card-markdown ::ng-deep .semantic-markdown pre {
        padding: 0.35rem !important;
        margin: 0.25rem 0 !important;
        font-size: 10px !important;
      }
    `,
  ],
})
export class DynamicAppViewerComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly authStore = inject(AuthStore);
  private readonly projectDataService = inject(ProjectDataService);
  private readonly browserStorage = inject(BrowserStorageManagementService);
  private readonly definitionController = inject(DefinitionControllerService); // Native Definition Controller
  private readonly fileManagementController = inject(FileManagementControllerService);
  private readonly commentController = inject(CommentControllerService);
  private readonly httpClient = inject(HttpClient);
  private readonly store = inject(Store);
  private readonly notificationService = inject(NotificationService);
  private readonly e2eRunner = inject(E2eTestRunnerService);
  private readonly elementRef = inject(ElementRef);

  public readonly configTexts = DYNAMIC_APP_VIEWER_CONFIG.texts;

  public viewerMode = signal<'installed' | 'builder'>('builder');
  public installedManifestPages = signal<any[]>([]);
  public appName = signal<string>('');
  public appVersion = signal<string | null>(null);
  public showGuidanceModal = signal<boolean>(false);

  // App State Signals
  public appConfig = signal<AppConfig | null>(null);
  public activePageConfig = signal<PageConfig | null>(null);
  public activeManifest = signal<any>(null);

  /** * @property {Signal<string | null>} activePageType
   * @description Tracks the architectural system type of the current page layout context ('AppPage' or 'AppDynamicPage').
   */
  public activePageType = signal<string | null>(null);

  /** * @property {Signal<string | null>} activePageId
   * @description Stores the UUID of the active page file definition, consumed directly by the static sandbox runtime layer.
   */
  public activePageId = signal<string | null>(null);

  /** * @property {Signal<string | null>} activePageRoute
   * @description Stores the human-readable route or page key of the active page for task and bubble scoping.
   */
  public activePageRoute = signal<string | null>(null);

  /** * @property {Signal<string | null>} activePageUrl
   * @description Stores the CDN URL of the active page for installed apps.
   */
  public activePageUrl = signal<string | null>(null);

  /** * @property {Signal<string>} activePageFileName
   * @description Stores the file name/path of the active page for definition-based or GitHub-backed apps.
   */
  public activePageFileName = signal<string>('pageTemplate.html');

  /** * @property {Signal<string>} activeAssetBaseUrl
   * @description Stores the base URL for resolving relative assets (like /assets/) inside the HTML file.
   */
  public activeAssetBaseUrl = signal<string>('');

  /** Active semantic application theme resolved from workspace installed application settings (e.g. 'default', 'obsidian-compact'). */
  public activeAppTheme = signal<string>('default');

  private registryChangedSub?: Subscription;

  // Route State Signals
  public currentAppId = signal<string | null>(null);
  public currentAppDef = signal<any | null>(null);
  private currentPageKey = signal<string | null>(null);
  private currentAction = signal<string | null>(null);
  public currentQueryParams = signal<Record<string, any>>({});
  public currentRouteParams = signal<Record<string, string>>({});
  private routeInitialized = signal<boolean>(false);

  // Caching & Lazy Task Loading Tracking
  private loadedContextKey: string | null = null;
  private loadedManifestUrl: string | null = null;
  private tasksLoadedForAppId: string | null = null;

  // UI Tracking Signals
  public isLoading = signal<boolean>(true);
  public error = signal<string | null>(null);

  // Sidebar State Signals
  public sidebarSplitSize = signal<number | string>('65%');
  public isSidebarOpen = signal<boolean>(false);
  public activeSidebarTab = signal<
    'Tasks' | 'Bug Report' | 'Feature Request' | 'Artifacts' | 'E2E Tests'
  >('Tasks');

  // E2E Test Mode State
  public testMode = signal<boolean>(false);

  /** Computed: Manifest dataModels passed down to enforce entity allowlisting on window.rapiderApi calls. */
  public effectiveDataModels = computed<any[]>(() => {
    const manifest = this.activeManifest();
    const appDef = this.currentAppDef();
    return (
      manifest?.dataModels ||
      appDef?.value?.dataModels ||
      appDef?.dataModels ||
      []
    );
  });

  /**
   * Computed: Enables allow-same-origin ONLY when developer/DOM-inspection tools are active
   * (Builder mode, E2E test execution/tab, Bug-pinning mode, or Tasks sidebar bubbles).
   * In standard Installed App viewing (/app/:appId/:pageKey), this evaluates to false so the
   * iframe runs in a strict null-origin sandbox with zero access to parent DOM or JWT localStorage.
   */
  public needsIframeDomAccess = computed<boolean>(() => {
    if (this.viewerMode() === 'builder') return true;
    if (this.testMode() || this.isAddCommentMode()) return true;
    if (this.isSidebarOpen()) {
      const tab = this.activeSidebarTab();
      if (tab === 'Tasks' || tab === 'E2E Tests' || tab === 'Bug Report') {
        return true;
      }
    }
    return false;
  });
  public captureScreenshots = signal<boolean>(false);
  public captureScreenshotsOnFailure = signal<boolean>(false);
  public recordVideo = signal<boolean>(false);
  public isSettingsCollapsed = signal<boolean>(false);

  public onCaptureAllChange(val: boolean) {
    this.captureScreenshots.set(val);
    if (val) this.captureScreenshotsOnFailure.set(false);
  }

  public onCaptureFailureChange(val: boolean) {
    this.captureScreenshotsOnFailure.set(val);
    if (val) this.captureScreenshots.set(false);
  }

  // Feature Requests State
  public featureRequests = signal<any[]>([]);
  public requestTypeFilter = signal<string>('All');
  public requestStatusFilter = signal<string>('All');
  public filteredFeatureRequests = computed(() => {
    const typeFilter = this.requestTypeFilter();
    const statusFilter = this.requestStatusFilter();
    return this.featureRequests().filter((req) => {
      const typeMatch = typeFilter === 'All' || req.type === typeFilter;
      const statusMatch = statusFilter === 'All' || req.status === statusFilter;
      return typeMatch && statusMatch;
    });
  });

  public isSubmittingFeatureRequest = signal<boolean>(false);
  public newRequestTitle = signal<string>('');
  public newRequestDescription = signal<string>('');
  public newRequestType = signal<string>('bug');

  // Unified Tasks State (Merged Bugs & Features)
  public readonly commentsPageSize = 20;
  public comments = signal<Comment[]>([]);
  public isLoadingComments = signal<boolean>(false);
  public isLoadingMoreComments = signal<boolean>(false);
  public hasMoreComments = signal<boolean>(false);
  public totalCommentsCount = signal<number | null>(null);
  public taskSearchQuery = signal<string>('');
  public taskTypeFilter = signal<string>('ALL');
  public taskStatusFilter = signal<string>('ALL');
  public selectedTaskStatuses = signal<string[]>([]);
  public reportedByMeOnly = signal<boolean>(false);
  public isTaskFilterDropdownOpen = signal<boolean>(false);
  public loadedTasksLimit = signal<number>(20);
  public selectedTaskForDetail = signal<UnifiedTaskItem | null>(null);
  public selectedTaskId = signal<string | null>(null);
  public taskToDelete = signal<UnifiedTaskItem | null>(null);

  // Task Inline Edit State (RPDR-613: Editable while Open)
  public isEditingTaskDetail = signal<boolean>(false);
  public editTaskTitle = signal<string>('');
  public editTaskDescription = signal<string>('');
  public editTaskType = signal<'Bug' | 'Feature'>('Bug');
  public isSavingTaskEdit = signal<boolean>(false);

  /**
   * Unified status filter options for the task management filter pills & multi-select dropdown.
   */
  public readonly taskStatusOptions: readonly string[] = [
    'Open',
    'Accepted',
    'In Progress',
    'In Review',
    'Ready for Release',
    'Released',
    'Confirmed',
    'Reopened',
    'Closed',
  ];

  public readonly taskStatusFilterMeta: ReadonlyArray<{
    value: string;
    label: string;
    iconClass: string;
    iconColorClass: string;
  }> = [
    { value: 'Open', label: 'Open', iconClass: 'fa-regular fa-circle-dot', iconColorClass: 'text-gray-500' },
    { value: 'Accepted', label: 'Accepted', iconClass: 'fa-solid fa-check', iconColorClass: 'text-blue-500' },
    { value: 'In Progress', label: 'In Progress', iconClass: 'fa-solid fa-clock', iconColorClass: 'text-sky-500' },
    { value: 'In Review', label: 'In Review', iconClass: 'fa-solid fa-clipboard-check', iconColorClass: 'text-purple-500' },
    { value: 'Ready for Release', label: 'Ready for Release', iconClass: 'fa-solid fa-tag', iconColorClass: 'text-amber-500' },
    { value: 'Released', label: 'Released', iconClass: 'fa-solid fa-rocket', iconColorClass: 'text-blue-600' },
    { value: 'Confirmed', label: 'Confirmed', iconClass: 'fa-solid fa-circle-check', iconColorClass: 'text-emerald-500' },
    { value: 'Reopened', label: 'Reopened', iconClass: 'fa-solid fa-rotate-left', iconColorClass: 'text-rose-500' },
    { value: 'Closed', label: 'Closed', iconClass: 'fa-solid fa-circle-xmark', iconColorClass: 'text-zinc-500' },
  ];

  public activeDropdownFilterCount = computed<number>(() => {
    const statusCount =
      this.selectedTaskStatuses().length > 0
        ? this.selectedTaskStatuses().length
        : this.taskStatusFilter() !== 'ALL'
          ? 1
          : 0;
    return (this.reportedByMeOnly() ? 1 : 0) + statusCount;
  });

  /**
   * Returns Tailwind CSS classes for the unified status badge matching the pipeline picture.
   */
  public getTaskStatusClass(status?: string): Record<string, boolean> {
    const s = this.getNormalizedTaskStatus({ status } as any);
    return {
      'bg-gray-100 text-gray-700 border-gray-200': s === 'Open',
      'bg-blue-50 text-blue-700 border-blue-200': s === 'Accepted',
      'bg-sky-50 text-sky-700 border-sky-200': s === 'In Progress',
      'bg-purple-50 text-purple-700 border-purple-200': s === 'In Review',
      'bg-amber-50 text-amber-700 border-amber-200': s === 'Ready for Release',
      'bg-blue-100 text-blue-800 border-blue-300': s === 'Released',
      'bg-emerald-50 text-emerald-700 border-emerald-200': s === 'Confirmed',
      'bg-rose-50 text-rose-700 border-rose-200': s === 'Reopened',
      'bg-zinc-100 text-zinc-700 border-zinc-300': s === 'Closed',
    };
  }

  /**
   * Returns dot/indicator background color class for active filter tags.
   */
  public getTaskStatusDotClass(status?: string): Record<string, boolean> {
    const s = this.getNormalizedTaskStatus({ status } as any);
    return {
      'bg-gray-500': s === 'Open',
      'bg-blue-500': s === 'Accepted',
      'bg-sky-500': s === 'In Progress',
      'bg-purple-500': s === 'In Review',
      'bg-amber-500': s === 'Ready for Release',
      'bg-blue-600': s === 'Released',
      'bg-emerald-500': s === 'Confirmed',
      'bg-rose-500': s === 'Reopened',
      'bg-zinc-500': s === 'Closed',
    };
  }

  /**
   * Returns normalized display label for status.
   */
  public getDisplayStatus(status?: string): string {
    return this.getNormalizedTaskStatus({ status } as any);
  }

  // Edit Task Approval Modal State
  public isEditApprovalModalVisible = signal<boolean>(false);
  public editingApprovalTask = signal<UnifiedTaskItem | null>(null);
  public editingApprovalValue = signal<'approved' | 'rejected'>('approved');
  public editingApprovalInitialValue = signal<'approved' | 'rejected'>('approved');

  public taskReplies = signal<Comment[]>([]);
  public isLoadingReplies = signal<boolean>(false);
  public replyContent = signal<string>('');
  public isSubmittingReply = signal<boolean>(false);

  /**
   * Page scope filter ('CURRENT' to filter by active page, 'ALL' for entire app).
   */
  public taskPageFilter = signal<'CURRENT' | 'ALL'>('CURRENT');

  public setTaskPageFilter(filter: 'CURRENT' | 'ALL'): void {
    this.taskPageFilter.set(filter);
    this.loadedTasksLimit.set(20);
  }

  public clearPageFilter(): void {
    this.taskPageFilter.set('CURRENT');
    this.loadedTasksLimit.set(20);
  }

  /**
   * Legacy Approval filter options kept for backward compatibility.
   */
  public taskApprovalFilter = signal<'ALL' | 'APPROVED' | 'PENDING' | 'REJECTED'>('ALL');

  public setTaskApprovalFilter(filter: 'ALL' | 'APPROVED' | 'PENDING' | 'REJECTED'): void {
    this.taskApprovalFilter.set(filter);
    this.loadedTasksLimit.set(20);
  }

  public clearApprovalFilter(): void {
    this.taskApprovalFilter.set('ALL');
  }

  /**
   * Computed boolean flag indicating whether any task filter (search query, type, status, reportedByMe, or page) is actively filtering.
   */
  public hasActiveFilters = computed<boolean>(() => {
    return (
      !!this.taskSearchQuery().trim() ||
      this.taskTypeFilter() !== 'ALL' ||
      this.selectedTaskStatuses().length > 0 ||
      this.taskStatusFilter() !== 'ALL' ||
      this.reportedByMeOnly() ||
      this.taskPageFilter() !== 'CURRENT'
    );
  });

  // DOM Element Tagging & Drop Bubble State
  @ViewChild('canvasWrapper') canvasWrapper?: ElementRef<HTMLElement>;
  @ViewChild('captureZone') captureZone?: ElementRef<HTMLElement>;
  @ViewChild('appSplitter') appSplitter?: NzSplitterComponent;

  public sidebarWidthPx = signal<number>(450);
  private appResizeObserver?: ResizeObserver;

  public isAddCommentMode = signal<boolean>(false);
  public bubblePositions = signal<
    Record<string, { top: number; left: number; isOrphaned: boolean }>
  >({});
  public pinnedMetadata = signal<{
    elementId?: string;
    uniqueSelector?: string;
    offsetX?: number;
    offsetY?: number;
    absoluteX?: number;
    absoluteY?: number;
    pageId?: string;
    tag?: string;
    x?: number;
    y?: number;
  } | null>(null);
  public showBubbles = signal<boolean>(true);
  /**
   * Tracks whether the canvas or its child frame/content is actively scrolling.
   * When true, task bubbles are temporarily hidden to prevent showing them in stale positions.
   */
  public isCanvasScrolling = signal<boolean>(false);

  private boundIframeDoc: Document | null = null;
  private iframePollInterval: any;
  private scrollTimeout: any;

  // Task Creation Modal State
  public isCreateTaskModalVisible = signal<boolean>(false);
  private isPinningFromModal = false;
  public draftTaskTitle = signal<string>('');
  public draftTaskDescription = signal<string>('');
  public draftTaskType = signal<'Bug' | 'Feature'>('Bug');
  public isSubmittingTask = signal<boolean>(false);

  // Rejection Reason Modal Signals
  public isRejectModalVisible = signal<boolean>(false);
  public rejectionReason = signal<string>('');
  public taskPendingRejection = signal<UnifiedTaskItem | null>(null);
  public isSubmittingRejection = signal<boolean>(false);

  /**
   * Helper to parse bubble coordinates from Comment metadata.
   */
  private parseCommentCoordinates(
    comment: Comment,
  ): any | null {
    if (!comment || !comment.metadata) return null;
    let rawMeta = comment.metadata as any;
    if (typeof rawMeta === 'string') {
      try {
        rawMeta = JSON.parse(rawMeta);
      } catch {
        return null;
      }
    }
    const metaObj: Record<string, any> = {};
    if (Array.isArray(rawMeta)) {
      for (const m of rawMeta) {
        if (m && m.key) metaObj[m.key] = m.value;
      }
    } else if (typeof rawMeta === 'object' && rawMeta !== null) {
      Object.assign(metaObj, rawMeta);
    } else {
      return null;
    }
    const x = parseFloat(metaObj['absoluteX'] ?? metaObj['offsetX'] ?? metaObj['x'] ?? metaObj['left']);
    const y = parseFloat(metaObj['absoluteY'] ?? metaObj['offsetY'] ?? metaObj['y'] ?? metaObj['top']);
    if (!isNaN(x) && !isNaN(y)) {
      return {
        x,
        y,
        pageId: metaObj['pageId'] || metaObj['page'] || metaObj['route'] || metaObj['pageKey'],
        elementId: metaObj['elementId'],
        uniqueSelector: metaObj['uniqueSelector'],
        offsetX: parseFloat(metaObj['offsetX'] || '0'),
        offsetY: parseFloat(metaObj['offsetY'] || '0'),
        tag: metaObj['tag'],
      };
    }
    return null;
  }

  /**
   * Helper to parse bubble coordinates from Feature Request metadata.
   */
  private parseRequestCoordinates(
    req: any,
  ): any | null {
    if (!req) return null;
    if (req.metadata) {
      if (typeof req.metadata === 'object') {
        return req.metadata;
      }
      try {
        const parsed = JSON.parse(req.metadata);
        if (parsed) return parsed;
      } catch {}
    }
    return null;
  }

  /**
   * Helper to parse unified task metadata regardless of task type.
   */
  public parseTaskMetadata(task: UnifiedTaskItem): any | null {
    const raw = task?.rawItem;
    if (!raw) return null;

    if (raw.metadata) {
      let rawMeta = raw.metadata as any;
      if (typeof rawMeta === 'string') {
        try {
          rawMeta = JSON.parse(rawMeta);
        } catch {
          return null;
        }
      }
      if (Array.isArray(rawMeta)) {
        const metaObj: Record<string, string> = {};
        for (const m of rawMeta) {
          if (m && m.key) metaObj[m.key] = m.value;
        }
        return metaObj;
      }
      if (typeof rawMeta === 'object' && rawMeta !== null) {
        return rawMeta;
      }
    }

    return this.parseRequestCoordinates(raw);
  }

  private extractTaskTitleAndDescription(
    rawContent: string,
    metaTitle?: string,
    metaDescription?: string,
    defaultTitle: string = 'Task',
  ): { title: string; description: string } {
    const content = (rawContent || '').trim();

    // 1. If explicit metadata exists for description, use it directly
    if (metaDescription !== undefined && metaDescription !== null && metaDescription.trim() !== '') {
      const explicitTitle = metaTitle?.trim();
      const firstLine = content.split('\n')[0]?.replace(/^#+\s*/, '').trim() || '';
      return {
        title: explicitTitle || firstLine || defaultTitle,
        description: metaDescription.trim(),
      };
    }

    const lines = content.split('\n');
    const rawFirstLine = lines[0]?.trim() || '';
    const cleanFirstLine = rawFirstLine.replace(/^#+\s*/, '').trim();

    // Explicit title from metadata takes precedence; otherwise use clean first line
    const title = metaTitle?.trim() || cleanFirstLine || defaultTitle;

    // 2. Extract description cleanly without duplicating the title
    let description = '';

    // If explicit title exists and content does NOT start with that title,
    // then the content is already a distinct description
    if (metaTitle?.trim() && !content.toLowerCase().startsWith(metaTitle.trim().toLowerCase())) {
      description = content;
    } else if (metaTitle?.trim() && content.toLowerCase().startsWith(metaTitle.trim().toLowerCase())) {
      // Content begins with the title -> strip the title
      description = content.slice(metaTitle.trim().length).replace(/^\s*\n+/, '').trim();
    } else if (lines.length > 1) {
      // Multi-line content where the first line was used as the title
      let remainingLines = lines.slice(1);
      // If second line was a markdown setext heading underline (=== or ---), strip it
      if (remainingLines.length > 0 && /^[=-]{2,}\s*$/.test(remainingLines[0].trim())) {
        remainingLines = remainingLines.slice(1);
      }
      const remainder = remainingLines.join('\n').replace(/^\s*\n+/, '').trim();
      // If the remainder is identical to the title, do not duplicate it
      if (remainder.toLowerCase() !== title.toLowerCase()) {
        description = remainder;
      }
    } else {
      // Single line content where the whole text is already the title
      description = '';
    }

    // Final safety check: if description is identical to title, clear description
    if (description.toLowerCase() === title.toLowerCase()) {
      description = '';
    }

    return { title, description };
  }

  /**
   * Reactive signal merging Bug Reports (comments) and Feature Requests into a unified list,
   * sorted from newest to oldest by creation date, with sequential TSK-xxx display IDs.
   */
  public unifiedTasks = computed<UnifiedTaskItem[]>(() => {
    const commentsTasks: UnifiedTaskItem[] = (this.comments() || [])
      .filter((c) => !c.parentCommentId)
      .map(
        (c) => {
        const rawContentStr = String(c.content || '');
        const hasInlineTypePrefix = /^\[(Feature|Bug)\]\s+/i.test(rawContentStr);
        const inlinePrefixMatch = rawContentStr.match(/^\[(Feature|Bug)\]\s+/i);
        const isFeature = inlinePrefixMatch
          ? inlinePrefixMatch[1].toLowerCase() === 'feature'
          : c.category === 'Feature Request' ||
            c.category === 'Feature' ||
            c.category === 'New Feature';
        const taskType: 'Bug' | 'Feature' = isFeature ? 'Feature' : 'Bug';
        const cleanedContentStr = hasInlineTypePrefix
          ? rawContentStr.replace(/^\[(Feature|Bug)\]\s+/i, '')
          : rawContentStr;
        const meta = this.parseTaskMetadata({
          type: taskType,
          rawItem: c,
        } as any);
        const coords = this.parseCommentCoordinates(c);
        const avatarUrl = this.extractAvatarUrl(c);

        const parsed = this.extractTaskTitleAndDescription(
          cleanedContentStr,
          hasInlineTypePrefix ? undefined : meta?.title,
          hasInlineTypePrefix ? undefined : meta?.description,
          isFeature ? 'Feature Request' : 'Bug Report',
        );

        const taskPageId =
          meta?.pageId ||
          meta?.page ||
          meta?.route ||
          meta?.pageKey ||
          coords?.pageId ||
          (c as any)?.pageId ||
          (c as any)?.route;

        return {
          id: '',
          rawId: c.id || '',
          title: parsed.title,
          description: parsed.description,
          type: taskType,
          status: c.status || 'Open',
          isApproved: (c as any).isApproved ?? null,
          createdBy: this.formatAuthorName(c.createdBy),
          createdById: (c as any).createdById || (c as any).createdByPersonId || (c as any).userId || (c as any).authorId || c.createdBy,
          authorAvatarUrl: avatarUrl,
          createdAt: c.createdDate || new Date(),
          replyCount: (c as any).replyCount ?? (c as any).repliesCount ?? 0,
          lastReplierNames: (c as any).lastReplierNames ?? (c as any).lastRepliers ?? [],
          elementSelector: meta?.uniqueSelector || meta?.elementId || undefined,
          elementId: meta?.elementId || undefined,
          tag: meta?.tag || undefined,
          bubbleMetadata: coords || undefined,
          coordinates: coords || undefined,
          pageId: taskPageId || undefined,
          rawItem: c,
        };
      },
    );

    const features: UnifiedTaskItem[] = (this.featureRequests() || []).map(
      (r) => {
        const coords = this.parseRequestCoordinates(r);
        const meta = r.metadata || {};
        let status = r.status || 'Open';
        if (status === 'Requested') status = 'Open';
        else if (status === 'Completed') status = 'Resolved';
        else if (status === 'Declined') status = 'Closed';
        const avatarUrl = this.extractAvatarUrl(r);

        const parsed = this.extractTaskTitleAndDescription(
          r.description || '',
          r.title || meta?.title,
          meta?.description,
          'Feature Request',
        );

        const taskPageId =
          meta?.pageId ||
          meta?.page ||
          meta?.route ||
          meta?.pageKey ||
          coords?.pageId ||
          (r as any)?.pageId ||
          (r as any)?.route;

        return {
          id: '',
          rawId: r.id || '',
          title: parsed.title,
          description: parsed.description,
          type: 'Feature',
          rawType: r.type,
          status: status,
          isApproved: (r as any).isApproved ?? null,
          createdBy: this.formatAuthorName(r.authorName),
          createdById: (r as any).createdById || (r as any).createdByPersonId || (r as any).userId || (r as any).authorId || r.authorName,
          authorAvatarUrl: avatarUrl,
          createdAt: r.createdAt || r.createdDate || new Date(),
          elementSelector: meta?.uniqueSelector || meta?.elementId || undefined,
          elementId: meta?.elementId || undefined,
          tag: meta?.tag || undefined,
          bubbleMetadata: coords || undefined,
          coordinates: coords || undefined,
          pageId: taskPageId || undefined,
          rawItem: r,
        };
      },
    );

    // Merge and sort newest to oldest
    const combined = [...commentsTasks, ...features].sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );

    const total = combined.length;
    return combined.map((task, index) => ({
      ...task,
      id: `TSK-${101 + (total - 1 - index)}`,
      bubbleNumber: total - index,
    }));
  });

  /**
   * Formats the sequential bubble number so it always fits inside the bubble badge:
   * - 1..999: numeric string ("1" .. "999")
   * - 1000..3599: 1 letter + 2 digits ("A00" .. "Z99", where A00 = 1000, A99 = 1099, B00 = 1100, ..., Z99 = 3599)
   * - 3600..10359: 2 letters + 1 digit ("AA0" .. "ZZ9")
   * - 10360+: base-36 uppercase string
   */
  public formatBubbleNumber(num?: number | null): string {
    if (num === undefined || num === null || isNaN(num)) return '';
    const n = Math.max(0, Math.floor(num));
    if (n <= 999) {
      return String(n);
    }
    if (n <= 3599) {
      const offset = n - 1000;
      const letter = String.fromCharCode(65 + Math.floor(offset / 100));
      const digits = String(offset % 100).padStart(2, '0');
      return `${letter}${digits}`;
    }
    if (n <= 10359) {
      const offset = n - 3600;
      const pairIndex = Math.floor(offset / 10);
      const firstLetter = String.fromCharCode(65 + Math.floor(pairIndex / 26));
      const secondLetter = String.fromCharCode(65 + (pairIndex % 26));
      const digit = offset % 10;
      return `${firstLetter}${secondLetter}${digit}`;
    }
    return n.toString(36).toUpperCase();
  }

  /**
   * Resolves the task's recorded page identifier from direct property, coordinates, or metadata.
   */
  public getTaskPageIdentifier(task: UnifiedTaskItem | any): string | undefined {
    if (!task) return undefined;
    if (task.pageId) return task.pageId;
    if (task.coordinates?.pageId) return task.coordinates.pageId;
    if (task.bubbleMetadata?.pageId) return task.bubbleMetadata.pageId;

    const raw = task.rawItem || task;
    if (raw) {
      if (raw.pageId) return raw.pageId;
      if (raw.route) return raw.route;
      if (raw.page) return raw.page;
      if (raw.pageKey) return raw.pageKey;

      let rawMeta = raw.metadata;
      if (typeof rawMeta === 'string') {
        try {
          rawMeta = JSON.parse(rawMeta);
        } catch {}
      }
      if (Array.isArray(rawMeta)) {
        const found = rawMeta.find(
          (m: any) =>
            m &&
            (m.key === 'pageId' ||
              m.key === 'page' ||
              m.key === 'route' ||
              m.key === 'pageKey'),
        );
        if (found && found.value) return String(found.value);
      } else if (typeof rawMeta === 'object' && rawMeta !== null) {
        const val =
          rawMeta.pageId || rawMeta.page || rawMeta.route || rawMeta.pageKey;
        if (val) return String(val);
      }
    }
    return undefined;
  }

  /**
   * Finds the manifest definition matching the currently active page.
   */
  public getActivePageDefinition(): any | null {
    const activeRoute = this.activePageRoute() || this.activePageId();
    const pages = this.installedManifestPages() || [];
    if (!activeRoute) return pages[0] || null;

    const clean = activeRoute.trim().toLowerCase().replace(/^\/+|\/+$/g, '');
    return (
      pages.find((p: any) => {
        const pRoute = (p.route || '').toLowerCase().replace(/^\/+|\/+$/g, '');
        const pKey = (p.key || '').toLowerCase();
        const pId = (p.id || '').toLowerCase();
        const pName = (p.name || p.title || '').toLowerCase();
        return (
          pRoute === clean ||
          pKey === clean ||
          pId === clean ||
          pName === clean ||
          pRoute === `${clean}/:id` ||
          `${pRoute}/:id` === clean
        );
      }) || null
    );
  }

  /**
   * Computes all normalized tokens (route, key, id, name, htmlFilePath) for the active page.
   */
  public getActivePageTokens(): Set<string> {
    const tokens = new Set<string>();
    const addToken = (val: string | null | undefined) => {
      if (!val) return;
      const s = String(val).trim().toLowerCase();
      if (!s) return;
      tokens.add(s);
      const stripped = s.replace(/^\/+|\/+$/g, '');
      if (stripped) {
        tokens.add(stripped);
        const noParams = stripped.replace(/\/:(id|action|key).*$/i, '');
        if (noParams) tokens.add(noParams);
        const basename = stripped.split('/').pop()?.replace(/\.html$/i, '');
        if (basename) tokens.add(basename);
      }
    };

    addToken(this.activePageId());
    addToken(this.activePageRoute());
    addToken(this.activePageFileName());
    addToken(this.route.snapshot.paramMap.get('pageKey'));

    const activePageDef = this.getActivePageDefinition();
    if (activePageDef) {
      addToken(activePageDef.id);
      addToken(activePageDef.route);
      addToken(activePageDef.key);
      addToken(activePageDef.name);
      addToken(activePageDef.title);
      addToken(activePageDef.htmlFilePath);
    }

    return tokens;
  }

  /**
   * Determines whether a given task or bug belongs to the active page.
   */
  public isTaskOnActivePage(task: UnifiedTaskItem | any): boolean {
    const taskPage = this.getTaskPageIdentifier(task);
    if (!taskPage || !taskPage.trim()) {
      return false;
    }

    const tokens = this.getActivePageTokens();
    const rawPage = taskPage.trim().toLowerCase();
    const strippedPage = rawPage.replace(/^\/+|\/+$/g, '');
    const noParamsPage = strippedPage.replace(/\/:(id|action|key).*$/i, '');
    const basenamePage = strippedPage.split('/').pop()?.replace(/\.html$/i, '') || '';

    return (
      tokens.has(rawPage) ||
      tokens.has(strippedPage) ||
      tokens.has(noParamsPage) ||
      (basenamePage !== '' && tokens.has(basenamePage))
    );
  }

  /**
   * Returns a display friendly page name for a task.
   */
  public getTaskPageName(task: UnifiedTaskItem): string {
    const pageIdent = this.getTaskPageIdentifier(task);
    if (!pageIdent) return 'General';
    const clean = pageIdent.trim().toLowerCase().replace(/^\/+|\/+$/g, '');
    const pages = this.installedManifestPages() || [];
    const found = pages.find((p: any) => {
      const pRoute = (p.route || '').toLowerCase().replace(/^\/+|\/+$/g, '');
      const pKey = (p.key || '').toLowerCase();
      const pId = (p.id || '').toLowerCase();
      const pName = (p.name || p.title || '').toLowerCase();
      return (
        pRoute === clean ||
        pKey === clean ||
        pId === clean ||
        pName === clean ||
        pRoute === `${clean}/:id`
      );
    });
    return found?.name || found?.title || found?.route || pageIdent;
  }

  /**
   * Resolves the unified lifecycle status for a given task item.
   */
  public getNormalizedTaskStatus(task: UnifiedTaskItem | null): string {
    if (!task) return 'Open';
    const rawStatus = (task.status || '').trim();
    if (rawStatus === 'Reopened' || rawStatus === 'reopened' || rawStatus === 'Reporter Rejected' || rawStatus === 'reporter_rejected') {
      return 'Reopened';
    }
    if (rawStatus === 'Confirmed' || rawStatus === 'confirmed' || rawStatus === 'Resolved' || rawStatus === 'resolved' || rawStatus === 'Completed') {
      return 'Confirmed';
    }
    if (rawStatus === 'Released' || rawStatus === 'released') {
      return 'Released';
    }
    if (rawStatus === 'Ready for Release' || rawStatus === 'ready_for_release') {
      return 'Ready for Release';
    }
    if (rawStatus === 'In Review' || rawStatus === 'in_review' || rawStatus === 'Ready to Test' || rawStatus === 'ready_to_test') {
      return 'In Review';
    }
    if (rawStatus === 'In Progress' || rawStatus === 'in_progress') {
      return 'In Progress';
    }
    if (rawStatus === 'Accepted' || rawStatus === 'accepted' || rawStatus === 'Approved' || rawStatus === 'approved') {
      return 'Accepted';
    }
    if (rawStatus === 'Closed' || rawStatus === 'closed' || rawStatus === 'Declined' || task.isApproved === false) {
      return 'Closed';
    }
    if (task.isApproved === true) {
      return 'Accepted';
    }
    return 'Open';
  }

  /**
   * Filtered tasks based on active page scope, search query, type filter, reportedByMe, and multi-select status filter.
   */
  public filteredTasks = computed<UnifiedTaskItem[]>(() => {
    const query = this.taskSearchQuery().toLowerCase().trim();
    const typeFilter = this.taskTypeFilter();
    const statusFilter = this.taskStatusFilter();
    const selectedStatuses = this.selectedTaskStatuses();
    const onlyMine = this.reportedByMeOnly();
    const pageFilter = this.taskPageFilter();

    return this.unifiedTasks().filter((task) => {
      // Filter by active page when in CURRENT page mode
      if (pageFilter === 'CURRENT' && !this.isTaskOnActivePage(task)) {
        return false;
      }

      if (onlyMine && !this.isTaskReporter(task)) {
        return false;
      }

      const formattedBubble = this.formatBubbleNumber(task.bubbleNumber).toLowerCase();
      const rawBubble = String(task.bubbleNumber ?? '');
      const matchesSearch =
        !query ||
        task.title.toLowerCase().includes(query) ||
        task.id.toLowerCase().includes(query) ||
        formattedBubble.includes(query) ||
        rawBubble.includes(query) ||
        task.description.toLowerCase().includes(query) ||
        task.createdBy.toLowerCase().includes(query);

      const matchesType =
        typeFilter === 'ALL' || task.type === typeFilter;

      const normStatus = this.getNormalizedTaskStatus(task);
      const matchesStatus =
        selectedStatuses.length > 0
          ? selectedStatuses.includes(normStatus)
          : statusFilter === 'ALL' || normStatus === statusFilter;

      return matchesSearch && matchesType && matchesStatus;
    });
  });

  /**
   * Tasks to display in the card list (all tasks loaded from server matching active filters).
   */
  public displayedTasks = computed<UnifiedTaskItem[]>(() => {
    return this.filteredTasks();
  });

  /**
   * Fetches the next page of comments from the backend when the user clicks 'Load More'.
   */
  public loadMoreTasks(): void {
    this.fetchComments(true);
  }

  private baseTasksByReporter = computed<UnifiedTaskItem[]>(() => {
    const all = this.unifiedTasks();
    return this.reportedByMeOnly()
      ? all.filter((t) => this.isTaskReporter(t))
      : all;
  });

  public activePageTasksCount = computed<number>(() => {
    return this.baseTasksByReporter().filter((t) => this.isTaskOnActivePage(t)).length;
  });

  public totalAppTasksCount = computed<number>(() => {
    if (this.reportedByMeOnly()) {
      return this.baseTasksByReporter().length;
    }
    const serverTotal = this.totalCommentsCount();
    const loadedTotal = this.unifiedTasks().length;
    return serverTotal !== null && serverTotal > loadedTotal
      ? serverTotal
      : loadedTotal;
  });

  public totalTasksCount = computed(() => {
    if (this.taskPageFilter() === 'ALL') {
      return this.totalAppTasksCount();
    }
    return this.activePageTasksCount();
  });

  public bugTasksCount = computed(() => {
    const tasks =
      this.taskPageFilter() === 'ALL'
        ? this.baseTasksByReporter()
        : this.baseTasksByReporter().filter((t) => this.isTaskOnActivePage(t));
    return tasks.filter((t) => t.type === 'Bug').length;
  });

  public featureTasksCount = computed(() => {
    const tasks =
      this.taskPageFilter() === 'ALL'
        ? this.baseTasksByReporter()
        : this.baseTasksByReporter().filter((t) => this.isTaskOnActivePage(t));
    return tasks.filter((t) => t.type === 'Feature').length;
  });

  public tasksWithBubbles = computed(() => {
    return this.unifiedTasks().filter((t) => !!t.bubbleMetadata);
  });

  // AI Actions State
  public aiActions = signal<any[]>([]);
  public isSubmittingAiAction = signal<boolean>(false);
  public newActionTitle = signal<string>('');
  public newActionBody = signal<string>('');

  constructor() {
    // Effect to reactively load the application context when both route parameters and workspace ID are available.
    // This perfectly resolves deep-linking and browser refresh issues where the auth store hydrates slightly after route initialization.
    effect(() => {
      let projectId = this.authStore.activeProject()?.id;

      // FALLBACK: If auth store is hydrating, attempt immediate read from local storage for faster paint
      if (!projectId) {
        projectId =
          this.browserStorage.getItem<string>('projectId') ||
          this.browserStorage.getItem<string>('activeProjectId') ||
          undefined;
      }

      const appId = this.currentAppId();
      const pageKey = this.currentPageKey();
      const action = this.currentAction(); // Add currentAction
      const isInit = this.routeInitialized();

      if (!isInit) return;

      if (appId && projectId) {
        untracked(() => {
          this.loadDynamicAppAndPages(appId, projectId, pageKey, action);
        });
      } else if (!appId) {
        untracked(() => {
          this.error.set(this.configTexts.noAppId);
          this.isLoading.set(false);
        });
      } else {
        // appId exists, but projectId hasn't hydrated yet (browser refresh scenario).
        // Keep loading spinner active and wait for the signal update.
        untracked(() => {
          this.isLoading.set(true);
        });
      }
    });

    // Ensure nz-splitter's canvas panel remains elastic (auto-fills 100% available space) when the sidebar opens
    effect(() => {
      if (this.isSidebarOpen()) {
        setTimeout(() => this.applyAppSplitterElasticSizes(), 50);
      }
    });
  }

  ngOnInit(): void {
    // Reactively extract the appId and pageKey from the routing parameters.
    // Pushes values into state tracking signals to be picked up by the constructor effect.
    this.route.paramMap.subscribe((params) => {
      this.currentAppId.set(params.get('appId'));
      this.currentPageKey.set(params.get('pageKey'));

      let action = params.get('action');
      const entityId = params.get('entityId');

      // If we have an entityId but no action specified, default to 'detail' view
      if (!action && entityId) {
        action = 'detail';
      }

      this.currentAction.set(action);

      const allParams: Record<string, string> = {};
      params.keys.forEach((key) => {
        allParams[key] = params.get(key) || '';
      });

      // Ensure the injected action is available in route params for the UI context
      if (action) {
        allParams['action'] = action;
      }

      // Map entityId to id to support older CRUD page bindings that rely on params.id
      if (entityId) {
        allParams['id'] = entityId;
      }

      this.currentRouteParams.set(allParams);

      // Un-collapse settings when navigating to a new page
      this.isSettingsCollapsed.set(false);

      this.routeInitialized.set(true);
    });

    this.route.data.subscribe((data) => {
      if (data['mode']) {
        this.viewerMode.set(data['mode']);
      }
    });

    // Reactively extract and track query parameters to pass down as a dynamic payload
    this.route.queryParams.subscribe((qParams) => {
      this.currentQueryParams.set({ ...qParams });
    });

    // Reactively sync application theme when workspace Application Settings change
    this.registryChangedSub =
      this.projectDataService.applicationRegistryChanged$.subscribe(() => {
        const projectId = this.authStore.activeProject()?.id;
        const appId = this.currentAppId();
        if (projectId && appId) {
          this.syncWorkspaceAppTheme(
            projectId,
            appId,
            this.activeManifest()?.theme ||
              this.currentAppDef()?.value?.theme ||
              this.currentAppDef()?.theme,
          );
        }
      });
  }

  private syncWorkspaceAppTheme(
    projectId: string,
    appId: string,
    fallbackTheme?: string,
  ): void {
    if (!projectId) {
      this.activeAppTheme.set(normalizeAppThemeKey(fallbackTheme));
      return;
    }
    this.projectDataService.getProjectApplications(projectId).subscribe({
      next: (apps: any[]) => {
        const def = this.currentAppDef();
        const matched = (apps || []).find(
          (a: any) =>
            !a.isDeleted &&
            (a.applicationKey === appId ||
              a.applicationDefinitionId === appId ||
              (def?.id && a.applicationDefinitionId === def.id) ||
              (def?.key && a.applicationKey === def.key)),
        );
        const resolved = normalizeAppThemeKey(
          matched?.theme ?? matched?.value?.theme ?? fallbackTheme ?? 'default',
        );
        this.activeAppTheme.set(resolved);
      },
      error: () => {
        this.activeAppTheme.set(normalizeAppThemeKey(fallbackTheme));
      },
    });
  }

  public getActivePageName(): string | undefined {
    const activeRoute = this.activePageRoute() || this.activePageId();
    if (!activeRoute) return undefined;
    const clean = activeRoute.trim().toLowerCase().replace(/^\/+|\/+$/g, '');
    const page = this.installedManifestPages().find((p) => {
      const pRoute = (p.route || '').toLowerCase().replace(/^\/+|\/+$/g, '');
      const pKey = (p.key || '').toLowerCase();
      const pId = (p.id || '').toLowerCase();
      const pName = (p.name || p.title || '').toLowerCase();
      return (
        pRoute === clean ||
        pKey === clean ||
        pId === clean ||
        pName === clean ||
        pRoute === `${clean}/:id` ||
        `${pRoute}/:id` === clean
      );
    });
    return page?.name || page?.title || activeRoute;
  }

  /**
   * Fetches the AppConfig JSON payload directly from the project definitions API
   * and subsequently cascades to resolve the dependent application pages securely.
   * @param {string} appId - The unique string routing identifier of the application.
   * @param {string} projectId - Active workspace ID.
   * @param {string | null} pageKey - The unique key of the requested page.
   * @param {string | null} action - Optional sub-action (e.g. 'detail') in deep links.
   */
  private loadDynamicAppAndPages(
    appId: string,
    projectId: string,
    pageKey: string | null,
    action: string | null = null,
  ): void {
    const mode = this.viewerMode();
    const nextContextKey = `${projectId}:${appId}:${mode}`;
    const isSameAppContext = this.loadedContextKey === nextContextKey;

    this.error.set(null);

    if (!isSameAppContext) {
      this.isLoading.set(true);
      this.appVersion.set(null);
      this.loadedContextKey = null;
      this.loadedManifestUrl = null;
      this.tasksLoadedForAppId = null;
      this.comments.set([]);
      this.featureRequests.set([]);
      this.totalCommentsCount.set(0);
      this.hasMoreComments.set(false);
    }

    // APPROACH 1: DEVELOPING (Builder Mode) -> Always load latest files from GitHub
    if (mode === 'builder') {
      this.loadBuilderApp(appId, projectId, pageKey, action, isSameAppContext);
      return;
    }

    // APPROACH 2: INSTALLED FROM APP STORE / SEEING APPLICATION -> Read S3 / Public URLs of released version
    this.loadInstalledApp(appId, projectId, pageKey, action, isSameAppContext);
  }

  /**
   * Loads the application in development mode (App Builder / Preview).
   * Strictly resolves the latest HTML templates from GitHub to reflect in-progress code edits.
   */
  private loadBuilderApp(
    appId: string,
    projectId: string,
    pageKey: string | null,
    action: string | null = null,
    isSameAppContext = false,
  ): void {
    const handleAppDef = (appDef: any) => {
      if (!appDef) {
        this.error.set(this.configTexts.loadFailed);
        this.isLoading.set(false);
        return;
      }

      this.appName.set(appDef.value?.name || appDef.name || 'App');
      const builderVer = appDef.value?.version || appDef.version;
      if (builderVer) {
        this.appVersion.set(builderVer);
      }
      this.currentAppDef.set(appDef);
      this.loadedContextKey = `${projectId}:${appId}:builder`;
      this.syncWorkspaceAppTheme(
        projectId,
        appId,
        appDef.value?.theme || appDef.theme,
      );

      // Lazy-load tasks ONLY when the Tasks panel is open
      if (this.isSidebarOpen() && this.activeSidebarTab() === 'Tasks') {
        this.ensureTasksLoaded();
      }

      const resolvedAssetBaseUrl = this.resolveAssetBaseUrl(appDef, appId);
      this.activeAssetBaseUrl.set(resolvedAssetBaseUrl);

      const webPages =
        appDef.value?.webPages ||
        appDef.webPages ||
        appDef.value?.pages ||
        appDef.pages;

      if (Array.isArray(webPages) && webPages.length > 0) {
        const sortedPages = [...webPages].sort((a: any, b: any) => {
          const orderA = a.priorityIndex ?? a.order ?? 1000;
          const orderB = b.priorityIndex ?? b.order ?? 1000;
          return orderA - orderB;
        });

        this.installedManifestPages.set(sortedPages);

        if (!pageKey) {
          const firstPage = sortedPages[0];
          const targetRoute = firstPage.route || firstPage.id || firstPage.key;
          this.router.navigate(
            ['/app-builder', appId, 'preview', targetRoute],
            { replaceUrl: true },
          );
          return;
        }

        const cleanKey = pageKey.trim();
        const decodedKey = decodeURIComponent(cleanKey);

        let searchKey = cleanKey;
        if (action) {
          searchKey = `${cleanKey}/${action}`;
        }
        const decodedSearchKey = decodeURIComponent(searchKey);
        const searchKeyDash = action ? `${cleanKey}-${action}` : cleanKey;
        const decodedSearchKeyDash = decodeURIComponent(searchKeyDash);

        let targetPageDef = sortedPages.find(
          (p: any) =>
            p.route === searchKey ||
            p.route === decodedSearchKey ||
            p.route === searchKeyDash ||
            p.route === decodedSearchKeyDash ||
            p.id === searchKey ||
            p.key === searchKey ||
            p.name?.toLowerCase() === searchKey.toLowerCase() ||
            p.name?.toLowerCase() === searchKeyDash.toLowerCase(),
        );

        if (!targetPageDef) {
          targetPageDef = sortedPages.find(
            (p: any) => p.route === `${cleanKey}/:id`,
          );
        }

        if (!targetPageDef) {
          targetPageDef = sortedPages.find(
            (p: any) =>
              // Fallbacks for original key without action
              p.route === cleanKey ||
              p.route === decodedKey ||
              p.id === cleanKey ||
              p.key === cleanKey ||
              p.name?.toLowerCase() === cleanKey.toLowerCase(),
          );
        }

        targetPageDef = targetPageDef || sortedPages[0];

        // DEVELOPING: Always read from GitHub via Definition preview, bypass S3
        const pageIdent = targetPageDef.route || targetPageDef.key || targetPageDef.id || cleanKey;
        this.activePageRoute.set(pageIdent);
        this.activePageId.set(appDef.id || appDef._id);
        this.activePageType.set('AppPage');
        this.activePageUrl.set(null);
        this.activePageFileName.set(
          targetPageDef.htmlFilePath ||
            targetPageDef.route ||
            `${cleanKey}.html`,
        );
        this.bubblePositions.set({});
        this.isLoading.set(false);
        if (this.isSidebarOpen() && this.activeSidebarTab() === 'Tasks') {
          setTimeout(() => this.recalculateBubblePositions(), 200);
        }
        return;
      }

      this.error.set(this.configTexts.noPages);
      this.isLoading.set(false);
    };

    // Fast path: reuse already-loaded app definition when switching pages within the same app
    if (isSameAppContext && this.currentAppDef()) {
      handleAppDef(this.currentAppDef());
      return;
    }

    this.isLoading.set(true);
    this.definitionController.findById({ id: appId }).subscribe({
      next: (def) => handleAppDef(def),
      error: () => {
        this.error.set(this.configTexts.loadFailed);
        this.isLoading.set(false);
      },
    });
  }

  /**
   * Loads the application in installed mode (App Store / Released View).
   * Prioritizes reading the released manifest and S3 / CDN public URLs.
   */
  private loadInstalledApp(
    appId: string,
    projectId: string,
    pageKey: string | null,
    action: string | null = null,
    isSameAppContext = false,
  ): void {
    // Fast path: reuse already-loaded manifest & appDef when switching pages within the same installed app
    if (isSameAppContext && this.currentAppDef() && this.activeManifest()) {
      const cachedDef = this.currentAppDef();
      this.processManifest(
        this.activeManifest(),
        cachedDef.applicationDefinitionId || appId,
        pageKey,
        action,
        this.loadedManifestUrl || cachedDef.manifestUrl,
      );
      if (this.isSidebarOpen() && this.activeSidebarTab() === 'Tasks') {
        this.ensureTasksLoaded();
        setTimeout(() => this.recalculateBubblePositions(), 200);
      }
      return;
    }

    this.isLoading.set(true);

    // 1. Check App Store installed applications registry first
    this.projectDataService.getProjectApplications(projectId).subscribe({
      next: (apps: any[]) => {
        const appDef =
          apps && apps.length > 0
            ? apps.find(
                (a: any) =>
                  !a.isDeleted &&
                  (a.applicationKey === appId ||
                    a.applicationDefinitionId === appId),
              )
            : null;

        if (appDef) {
          const isVisible = isApplicationVisibleToUser(
            appDef,
            this.authStore.activeProject(),
            this.authStore.activePerson(),
            this.authStore.user(),
          );
          if (!isVisible) {
            this.error.set(
              'This application is currently in development mode and is only accessible to the workspace owner and app developer.',
            );
            this.isLoading.set(false);
            return;
          }
        }

        if (appDef && appDef.manifestUrl) {
          if (!isTrustedAppResourceUrl(appDef.manifestUrl)) {
            console.error(
              '[DynamicAppViewer] Blocked untrusted manifestUrl:',
              appDef.manifestUrl,
            );
            this.error.set('Blocked untrusted application manifest URL.');
            this.isLoading.set(false);
            return;
          }
          this.appName.set(appDef.name || 'App');
          const installedVer =
            appDef.activeVersion ||
            appDef.installedVersion ||
            appDef.version ||
            appDef.value?.version;
          if (installedVer) {
            this.appVersion.set(installedVer);
          }
          this.currentAppDef.set(appDef);
          this.activeAppTheme.set(
            normalizeAppThemeKey(
              appDef.theme ?? appDef.value?.theme ?? 'default',
            ),
          );
          this.loadedContextKey = `${projectId}:${appId}:installed`;
          this.loadedManifestUrl = appDef.manifestUrl;

          // Lazy-load tasks ONLY when the Tasks panel is open
          if (this.isSidebarOpen() && this.activeSidebarTab() === 'Tasks') {
            this.ensureTasksLoaded();
          }

          if (manifestCache.url === appDef.manifestUrl && manifestCache.data) {
            this.processManifest(
              manifestCache.data,
              appDef.applicationDefinitionId || appId,
              pageKey,
              action,
              appDef.manifestUrl,
            );
          } else {
            this.httpClient.get<any>(appDef.manifestUrl).subscribe({
              next: (manifest) => {
                manifestCache.url = appDef.manifestUrl;
                manifestCache.data = manifest;
                this.processManifest(
                  manifest,
                  appDef.applicationDefinitionId || appId,
                  pageKey,
                  action,
                  appDef.manifestUrl,
                );
              },
              error: () => {
                this.error.set('Could not load application manifest.');
                this.isLoading.set(false);
              },
            });
          }
          return;
        }

        this.error.set('Could not find installed application or manifest.');
        this.isLoading.set(false);
      },
      error: () => {
        this.error.set('Could not find installed application or manifest.');
        this.isLoading.set(false);
      },
    });
  }

  /**
   * Dynamically resolves the CDN asset base URL for the application.
   * Version numbers are never hardcoded and are always determined from the manifest,
   * published versions array, manifestUrl, or the application definition version.
   */
  private resolveAssetBaseUrl(
    appDef: any,
    appId?: string,
    manifestUrl?: string | null,
  ): string {
    if (!appDef && !manifestUrl) {
      return '';
    }

    // 1. Direct explicit assetBaseUrl property
    if (appDef?.value?.assetBaseUrl) {
      return appDef.value.assetBaseUrl;
    }
    if (appDef?.assetBaseUrl) {
      return appDef.assetBaseUrl;
    }

    // 2. From manifestUrl if provided or present on appDef (e.g. https://.../versions/{version}/manifest.json)
    const effectiveManifestUrl =
      manifestUrl || appDef?.manifestUrl || appDef?.value?.manifestUrl;
    if (effectiveManifestUrl && typeof effectiveManifestUrl === 'string') {
      const lastSlash = effectiveManifestUrl.lastIndexOf('/');
      if (lastSlash !== -1) {
        return `${effectiveManifestUrl.substring(0, lastSlash)}/assets`;
      }
    }

    // 3. From versions array (prefer latest published version, or latest entry)
    const versions = Array.isArray(appDef?.value?.versions)
      ? appDef.value.versions
      : Array.isArray(appDef?.versions)
        ? appDef.versions
        : [];

    if (versions.length > 0) {
      const published = versions.filter(
        (v: any) => v && v.status === 'published',
      );
      const latest =
        published.length > 0
          ? published[published.length - 1]
          : versions[versions.length - 1];

      if (latest?.assetBaseUrl) {
        return latest.assetBaseUrl;
      }
      if (latest?.manifestUrl && typeof latest.manifestUrl === 'string') {
        const lastSlash = latest.manifestUrl.lastIndexOf('/');
        if (lastSlash !== -1) {
          return `${latest.manifestUrl.substring(0, lastSlash)}/assets`;
        }
      }
      if (latest?.version) {
        const targetId =
          appDef?.id || appDef?._id || appDef?.applicationDefinitionId || appId;
        if (targetId) {
          return `https://rapider.nyc3.cdn.digitaloceanspaces.com/app/${targetId}/versions/${latest.version}/assets`;
        }
      }
    }

    // 4. From app definition top-level dynamic version
    const dynamicVersion = appDef?.value?.version || appDef?.version;
    const defId =
      appDef?.id || appDef?._id || appDef?.applicationDefinitionId || appId;
    if (defId && dynamicVersion) {
      return `https://rapider.nyc3.cdn.digitaloceanspaces.com/app/${defId}/versions/${dynamicVersion}/assets`;
    }

    return '';
  }

  private processManifest(
    manifest: any,
    appId: string,
    pageKey: string | null,
    action: string | null = null,
    manifestUrl: string | null = null,
  ): void {
    this.activeManifest.set(manifest);
    if (
      manifest?.theme &&
      normalizeAppThemeKey(this.currentAppDef()?.theme ?? this.currentAppDef()?.value?.theme) === 'default'
    ) {
      this.activeAppTheme.set(normalizeAppThemeKey(manifest.theme));
    }
    if (manifest?.version && !this.appVersion()) {
      this.appVersion.set(manifest.version);
    }
    if (!manifest || !manifest.pages || manifest.pages.length === 0) {
      this.error.set(this.configTexts.noPages);
      this.isLoading.set(false);
      return;
    }

    let baseCdnUrl = '';
    if (manifestUrl) {
      const lastSlashIndex = manifestUrl.lastIndexOf('/');
      baseCdnUrl =
        lastSlashIndex !== -1
          ? manifestUrl.substring(0, lastSlashIndex)
          : manifestUrl;
    }

    const resolvedAssetBaseUrl = this.resolveAssetBaseUrl(
      manifest,
      appId,
      manifestUrl,
    );
    this.activeAssetBaseUrl.set(
      resolvedAssetBaseUrl && isTrustedAppResourceUrl(resolvedAssetBaseUrl)
        ? resolvedAssetBaseUrl
        : '',
    );

    const mappedPages = manifest.pages.map((p: any) => {
      const rawHtmlUrl =
        p.publicUrl ||
        p.htmlUrl ||
        (p.htmlFilePath && baseCdnUrl
          ? `${baseCdnUrl}/${p.htmlFilePath}`
          : undefined);
      return {
        ...p,
        htmlUrl:
          rawHtmlUrl && isTrustedAppResourceUrl(rawHtmlUrl)
            ? rawHtmlUrl
            : undefined,
      };
    });

    this.installedManifestPages.set(mappedPages);

    const sortedPages = mappedPages.sort((a: any, b: any) => {
      const orderA = a.priorityIndex ?? a.order ?? 1000;
      const orderB = b.priorityIndex ?? b.order ?? 1000;
      return orderA - orderB;
    });

    if (!pageKey) {
      const firstPage = sortedPages[0];
      this.router.navigate(['/app', appId, firstPage.route], {
        replaceUrl: true,
      });
    } else {
      const cleanKey = pageKey.trim();
      const decodedKey = decodeURIComponent(cleanKey);

      let searchKey = cleanKey;
      if (action) {
        searchKey = `${cleanKey}/${action}`;
      }
      const decodedSearchKey = decodeURIComponent(searchKey);
      const searchKeyDash = action ? `${cleanKey}-${action}` : cleanKey;
      const decodedSearchKeyDash = decodeURIComponent(searchKeyDash);

      console.log(
        '[DynamicAppViewer] Installed Mode Available manifest pages:',
        sortedPages.map((p: any) => ({
          name: p.name,
          route: p.route,
          key: p.key,
          id: p.id,
        })),
      );
      console.log(
        '[DynamicAppViewer] Installed Mode Searching for manifest keys:',
        searchKey,
        searchKeyDash,
      );

      let targetPageDef = sortedPages.find(
        (p: any) =>
          p.route === searchKey ||
          p.route === decodedSearchKey ||
          p.route === searchKeyDash ||
          p.route === decodedSearchKeyDash ||
          p.id === searchKey ||
          p.key === searchKey ||
          p.name?.toLowerCase() === searchKey.toLowerCase() ||
          p.name?.toLowerCase() === searchKeyDash.toLowerCase(),
      );

      if (!targetPageDef) {
        targetPageDef = sortedPages.find(
          (p: any) => p.route === `${cleanKey}/:id`,
        );
      }

      if (!targetPageDef) {
        targetPageDef = sortedPages.find(
          (p: any) =>
            // Fallbacks for original key without action
            p.route === cleanKey ||
            p.route === decodedKey ||
            p.id === cleanKey ||
            p.key === cleanKey ||
            p.name?.toLowerCase() === cleanKey.toLowerCase(),
        );
      }

      targetPageDef = targetPageDef || sortedPages[0];
      const pageIdent = targetPageDef.route || targetPageDef.key || targetPageDef.id || cleanKey;

      const candidatePageUrl = targetPageDef.publicUrl || targetPageDef.htmlUrl;
      if (candidatePageUrl && !isTrustedAppResourceUrl(candidatePageUrl)) {
        console.error(
          '[DynamicAppViewer] Blocked untrusted page URL:',
          candidatePageUrl,
        );
        this.error.set('Blocked untrusted application page URL.');
        this.isLoading.set(false);
        return;
      }

      this.activePageId.set(pageIdent);
      this.activePageRoute.set(pageIdent);
      this.activePageType.set(targetPageDef.type || 'AppPage');
      this.activePageUrl.set(candidatePageUrl || null);
      this.bubblePositions.set({});
      this.isLoading.set(false);
    }
  }

  /**
   * Fetches the HTML template string from the repository using the SDK and updates the PageConfig dynamically.
   * @param {any} pageDef - The raw definition object returned from the API representing the specific page.
   */
  private loadPageTemplate(pageDef: any): void {
    const pageId = pageDef.id || pageDef.key;

    // Call the definition version control endpoint to read the template file directly
    this.definitionController
      .readFileVersion({
        id: pageId,
        extension: 'html',
        name: DefinitionFileNames.PageTemplate,
      })
      .subscribe({
        next: (templateHtml: string) => {
          // Overlay the fetched HTML string onto the finalized page configuration state
          const pageConfig: PageConfig = {
            pageId: pageId,
            pageName: pageDef.value?.name || pageDef.name,
            route: pageDef.value?.route || pageDef.key,
            dataVariables: pageDef.value?.dataVariables || [],
            dataTransformations: pageDef.value?.dataTransformations || [],
            events: pageDef.value?.events || [],
            template: templateHtml || '',
          };

          this.activePageConfig.set(pageConfig);
          this.isLoading.set(false);
        },
        error: (err: any) => {
          console.error('[DynamicAppViewer] Failed to load page template', err);
          // If it evaluates to a 404, the file hasn't been created yet, but we shouldn't necessarily block the entire app
          // Just let it load an empty template so the UI engine renders the wrapper successfully.
          if (err?.status === 404 || err?.status === 400) {
            const fallbackConfig: PageConfig = {
              pageId: pageId,
              pageName: pageDef.value?.name || pageDef.name,
              route: pageDef.value?.route || pageDef.key,
              dataVariables: pageDef.value?.dataVariables || [],
              dataTransformations: pageDef.value?.dataTransformations || [],
              events: pageDef.value?.events || [],
              template: '',
            };
            this.activePageConfig.set(fallbackConfig);
          } else {
            this.error.set(
              `${this.configTexts.templateLoadFailed} ("${pageId}")`,
            );
          }
          this.isLoading.set(false);
        },
      });
  }

  // Sidebar & Tasks Methods
  public toggleArtifactsSidebar(): void {
    if (this.isSidebarOpen() && this.activeSidebarTab() === 'Artifacts') {
      this.isSidebarOpen.set(false);
    } else {
      this.activeSidebarTab.set('Artifacts');
      this.isSidebarOpen.set(true);
    }
  }

  public openTasksSidebar(): void {
    if (this.isSidebarOpen() && this.activeSidebarTab() === 'Tasks') {
      this.isSidebarOpen.set(false);
    } else {
      this.activeSidebarTab.set('Tasks');
      this.isSidebarOpen.set(true);
      this.ensureTasksLoaded();
      setTimeout(() => {
        this.applyAppSplitterElasticSizes();
        this.recalculateBubblePositions();
      }, 50);
    }
  }

  /**
   * Updates nz-splitter's internal innerSizes signal so that the app preview canvas panel
   * stays elastic (size: undefined), immediately expanding or shrinking to fill 100% of the
   * remaining space when the container width changes (e.g. AI Assistant splitter drag or window resize).
   */
  public applyAppSplitterElasticSizes(): void {
    if (!this.appSplitter) return;
    const sidebarWidth = this.sidebarWidthPx();
    (this.appSplitter as any).innerSizes?.set([undefined, sidebarWidth]);
  }

  public onSidebarSplitResize(sizes: number[]): void {
    if (sizes && sizes.length >= 2) {
      const sidebarSize = sizes[1];
      if (sidebarSize >= 320) {
        this.sidebarWidthPx.set(sidebarSize);
      }
    }
  }

  public onSidebarSplitResizeEnd(sizes?: number[]): void {
    if (sizes && sizes.length >= 2) {
      const sidebarSize = sizes[1];
      if (sidebarSize >= 320) {
        this.sidebarWidthPx.set(sidebarSize);
      }
    }
    this.applyAppSplitterElasticSizes();
    this.recalculateBubblePositions();
  }

  public openE2ESidebar(): void {
    if (this.isSidebarOpen() && this.activeSidebarTab() === 'E2E Tests') {
      this.isSidebarOpen.set(false);
    } else {
      this.activeSidebarTab.set('E2E Tests');
      this.isSidebarOpen.set(true);
      this.isSettingsCollapsed.set(false);
      this.loadSavedReports();
    }
  }

  public toggleFeatureSidebar(): void {
    this.openTasksSidebar();
  }

  public setSidebarTab(
    tab: 'Tasks' | 'Bug Report' | 'Feature Request' | 'Artifacts' | 'E2E Tests',
  ): void {
    if (tab === 'Bug Report' || tab === 'Feature Request') {
      this.activeSidebarTab.set('Tasks');
    } else {
      this.activeSidebarTab.set(tab);
    }
    if (this.activeSidebarTab() === 'Tasks') {
      this.ensureTasksLoaded();
    } else if (tab === 'E2E Tests') {
      this.isSettingsCollapsed.set(false);
      this.loadSavedReports();
    }
  }

  /**
   * Ensures tasks (comments + feature requests) are fetched once for the active application
   * only when the Tasks panel is open.
   */
  private ensureTasksLoaded(force = false): void {
    const appId = this.currentAppId();
    if (!appId) return;
    const defId =
      this.currentAppDef()?.applicationDefinitionId ||
      this.currentAppDef()?.id ||
      '';
    const key = `${appId}:${defId}`;
    if (!force && this.tasksLoadedForAppId === key) {
      return;
    }
    this.tasksLoadedForAppId = key;
    this.fetchTasks();
  }

  /**
   * Fetches both comments (bugs) and feature requests for the current application.
   */
  public fetchTasks(): void {
    const appId = this.currentAppId();
    if (appId) {
      const defId =
        this.currentAppDef()?.applicationDefinitionId ||
        this.currentAppDef()?.id ||
        '';
      this.tasksLoadedForAppId = `${appId}:${defId}`;
    }
    this.fetchComments();
    this.fetchFeatureRequests();
  }

  /**
   * Resolves all possible string identifiers for the current application
   * (slug, routing key, definition ID, application definition ID).
   */
  public getRelatedAppIds(): string[] {
    const ids = new Set<string>();
    const appId = this.currentAppId();
    if (appId) ids.add(appId);

    const appDef = this.currentAppDef();
    if (appDef) {
      if (appDef.applicationDefinitionId) ids.add(appDef.applicationDefinitionId);
      if (appDef.id) ids.add(appDef.id);
      if (appDef._id) ids.add(appDef._id);
      if (appDef.applicationKey) ids.add(appDef.applicationKey);
      if (appDef.key) ids.add(appDef.key);
      if (appDef.route) ids.add(appDef.route);
      if (appDef.value?.route) ids.add(appDef.value.route);
      if (appDef.value?.key) ids.add(appDef.value.key);
      if (appDef.value?.appId) ids.add(appDef.value.appId);
    }
    return Array.from(ids).filter(Boolean);
  }

  /**
   * Retrieves bug report comments for the current app from CommentControllerService in batches of 20.
   *
   * @param isLoadMore - When true, fetches the next batch and appends it to existing comments;
   *                     otherwise resets and fetches the initial 20 comments.
   */
  public fetchComments(isLoadMore = false): void {
    const targetIds = this.getRelatedAppIds();
    if (!targetIds.length) return;

    if (isLoadMore) {
      if (this.isLoadingMoreComments() || !this.hasMoreComments()) {
        return;
      }
      this.isLoadingMoreComments.set(true);
    } else {
      this.isLoadingComments.set(true);
      this.hasMoreComments.set(false);
    }

    const currentSkip = isLoadMore ? this.comments().length : 0;

    const whereClause: any = {
      relatedEntityName: 'App',
      relatedEntityId: targetIds.length === 1 ? targetIds[0] : { inq: targetIds },
      or: [
        { parentCommentId: null },
        { parentCommentId: { exists: false } },
      ],
    };

    const filterObj = {
      where: whereClause,
      order: ['createdDate DESC'],
      limit: this.commentsPageSize,
      skip: currentSkip,
    };

    this.commentController
      .find({
        filter: JSON.stringify(filterObj),
      })
      .subscribe({
        next: (comments: Comment[]) => {
          const batch = comments || [];
          // If the returned batch has fewer items than the page size, there are no more comments on the server
          this.hasMoreComments.set(batch.length >= this.commentsPageSize);

          if (isLoadMore) {
            this.comments.update((existing) => [...existing, ...batch]);
            this.isLoadingMoreComments.set(false);
          } else {
            this.comments.set(batch);
            this.isLoadingComments.set(false);
          }

          this.recalculateBubblePositions();
        },
        error: (err) => {
          console.error('[DynamicAppViewer] Failed to fetch bug comments:', err);
          if (isLoadMore) {
            this.isLoadingMoreComments.set(false);
          } else {
            this.comments.set([]);
            this.isLoadingComments.set(false);
          }
        },
      });

    // On initial load, also query total comments count from the database
    if (!isLoadMore) {
      this.commentController
        .count({
          where: JSON.stringify(whereClause),
        })
        .subscribe({
          next: (res) => {
            if (res && typeof res.count === 'number') {
              this.totalCommentsCount.set(res.count);
            }
          },
          error: () => {},
        });
    }
  }

  // Feature Requests Methods
  public fetchFeatureRequests(): void {
    const targetAppId =
      this.currentAppDef()?.applicationDefinitionId ||
      this.currentAppDef()?.id ||
      this.currentAppId();
    if (!targetAppId) return;

    this.definitionController.getAppFeatureRequests({ id: targetAppId }).subscribe({
      next: (requests) => {
        // Sort newest to oldest
        const sorted = (requests || []).sort(
          (a: any, b: any) =>
            new Date(b.createdAt || b.createdDate || 0).getTime() -
            new Date(a.createdAt || a.createdDate || 0).getTime(),
        );
        this.featureRequests.set(sorted);
      },
      error: () => {
        this.featureRequests.set([]);
      },
    });
  }

  ngAfterViewInit(): void {
    this.setupIframeListeners();
    if (this.captureZone?.nativeElement) {
      this.captureZone.nativeElement.addEventListener('click', this.boundHandleClick, true);
      this.captureZone.nativeElement.addEventListener('mousedown', this.boundHandlePointerEvent, true);
      this.captureZone.nativeElement.addEventListener('pointerdown', this.boundHandlePointerEvent, true);
      this.captureZone.nativeElement.addEventListener('mouseup', this.boundHandlePointerEvent, true);
      this.captureZone.nativeElement.addEventListener('pointerup', this.boundHandlePointerEvent, true);
      this.captureZone.nativeElement.addEventListener('submit', this.boundHandlePointerEvent, true);
    }
    if (typeof ResizeObserver !== 'undefined' && this.elementRef?.nativeElement) {
      this.appResizeObserver = new ResizeObserver(() => {
        if (this.isSidebarOpen()) {
          this.applyAppSplitterElasticSizes();
        }
        this.recalculateBubblePositions();
      });
      this.appResizeObserver.observe(this.elementRef.nativeElement);
    }
    if (this.canvasWrapper?.nativeElement) {
      this.canvasWrapper.nativeElement.addEventListener(
        'scroll',
        this.boundHandleScroll,
        true,
      );
      this.canvasWrapper.nativeElement.addEventListener(
        'wheel',
        this.boundHandleWheel,
        { capture: true, passive: true },
      );
      this.canvasWrapper.nativeElement.addEventListener(
        'touchmove',
        this.boundHandleTouchMove,
        { capture: true, passive: true },
      );
    }
  }

  ngOnDestroy(): void {
    this.registryChangedSub?.unsubscribe();
    if (this.captureZone?.nativeElement) {
      this.captureZone.nativeElement.removeEventListener('click', this.boundHandleClick, true);
      this.captureZone.nativeElement.removeEventListener('mousedown', this.boundHandlePointerEvent, true);
      this.captureZone.nativeElement.removeEventListener('pointerdown', this.boundHandlePointerEvent, true);
      this.captureZone.nativeElement.removeEventListener('mouseup', this.boundHandlePointerEvent, true);
      this.captureZone.nativeElement.removeEventListener('pointerup', this.boundHandlePointerEvent, true);
      this.captureZone.nativeElement.removeEventListener('submit', this.boundHandlePointerEvent, true);
    }
    if (this.appResizeObserver) {
      this.appResizeObserver.disconnect();
    }
    if (this.iframePollInterval) {
      clearInterval(this.iframePollInterval);
    }
    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
    }
    if (this.canvasWrapper?.nativeElement) {
      this.canvasWrapper.nativeElement.removeEventListener(
        'scroll',
        this.boundHandleScroll,
        true,
      );
      this.canvasWrapper.nativeElement.removeEventListener(
        'wheel',
        this.boundHandleWheel,
        true,
      );
      this.canvasWrapper.nativeElement.removeEventListener(
        'touchmove',
        this.boundHandleTouchMove,
        true,
      );
    }
    this.removeIframeListeners();
  }

  @HostListener('window:resize')
  public onWindowResize(): void {
    if (this.isSidebarOpen()) {
      this.applyAppSplitterElasticSizes();
    }
    this.recalculateBubblePositions();
  }

  @HostListener('window:scroll')
  public onWindowScroll(): void {
    this.triggerScrollHiding();
  }

  /**
   * Temporarily hides the canvas bubbles during scrolling gestures, debouncing
   * the recalculation and reappearance of the bubbles until the scrolling has finished.
   */
  private triggerScrollHiding(): void {
    if (!this.isCanvasScrolling()) {
      this.isCanvasScrolling.set(true);
    }
    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
    }
    this.scrollTimeout = setTimeout(() => {
      this.recalculateBubblePositions();
      this.isCanvasScrolling.set(false);
    }, 150);
  }

  private boundHandlePointerEvent = (e: Event) => {
    if (
      this.isAddCommentMode() ||
      (typeof window !== 'undefined' && (window as any).__rapiderCommentModeActive)
    ) {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
    }
  };

  private boundHandleClick = (e: MouseEvent) => {
    if (
      this.isAddCommentMode() ||
      (typeof window !== 'undefined' && (window as any).__rapiderCommentModeActive)
    ) {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      this.handleCaptureZoneClick(e);
    }
  };

  private boundHandleMouseMove = (e: MouseEvent) => {
    if (this.isAddCommentMode()) {
      this.handleCaptureZoneMouseMove(e);
    }
  };

  private boundHandleMouseOut = (e: MouseEvent) => {
    if (this.isAddCommentMode()) {
      this.handleCaptureZoneMouseOut(e);
    }
  };

  private boundHandleScroll = (e: Event) => {
    this.triggerScrollHiding();
  };

  private boundHandleWheel = (e: Event) => {
    this.triggerScrollHiding();
  };

  private boundHandleTouchMove = (e: Event) => {
    this.triggerScrollHiding();
  };

  /**
   * Captures scroll events on the capture zone container directly from the template.
   *
   * @param _event The scroll event.
   */
  public onCaptureZoneScroll(_event: Event): void {
    this.triggerScrollHiding();
  }

  /**
   * Captures wheel events on the capture zone container directly from the template.
   *
   * @param _event The wheel event.
   */
  public onCaptureZoneWheel(_event: WheelEvent): void {
    this.triggerScrollHiding();
  }

  private setupIframeListeners(): void {
    this.iframePollInterval = setInterval(() => {
      if (!this.needsIframeDomAccess()) {
        return;
      }
      const iframe = this.captureZone?.nativeElement.querySelector(
        'iframe',
      ) as HTMLIFrameElement;
      let iframeDoc: Document | null = null;
      try {
        iframeDoc = iframe?.contentDocument || null;
      } catch {
        iframeDoc = null;
      }
      if (iframe && iframeDoc && iframeDoc !== this.boundIframeDoc) {
        this.removeIframeListeners();
        this.boundIframeDoc = iframeDoc;
        this.boundIframeDoc.addEventListener('click', this.boundHandleClick, true);
        this.boundIframeDoc.addEventListener('mousedown', this.boundHandlePointerEvent, true);
        this.boundIframeDoc.addEventListener('pointerdown', this.boundHandlePointerEvent, true);
        this.boundIframeDoc.addEventListener('mouseup', this.boundHandlePointerEvent, true);
        this.boundIframeDoc.addEventListener('pointerup', this.boundHandlePointerEvent, true);
        this.boundIframeDoc.addEventListener('submit', this.boundHandlePointerEvent, true);
        this.boundIframeDoc.addEventListener(
          'mousemove',
          this.boundHandleMouseMove,
          true,
        );
        this.boundIframeDoc.addEventListener(
          'mouseout',
          this.boundHandleMouseOut,
          true,
        );
        this.boundIframeDoc.addEventListener(
          'scroll',
          this.boundHandleScroll,
          true,
        );
        this.boundIframeDoc.addEventListener(
          'wheel',
          this.boundHandleWheel,
          { capture: true, passive: true },
        );
        this.boundIframeDoc.addEventListener(
          'touchmove',
          this.boundHandleTouchMove,
          { capture: true, passive: true },
        );
        if (this.boundIframeDoc.defaultView) {
          this.boundIframeDoc.defaultView.addEventListener('click', this.boundHandleClick, true);
          this.boundIframeDoc.defaultView.addEventListener('mousedown', this.boundHandlePointerEvent, true);
          this.boundIframeDoc.defaultView.addEventListener('pointerdown', this.boundHandlePointerEvent, true);
          this.boundIframeDoc.defaultView.addEventListener('mouseup', this.boundHandlePointerEvent, true);
          this.boundIframeDoc.defaultView.addEventListener('pointerup', this.boundHandlePointerEvent, true);
          this.boundIframeDoc.defaultView.addEventListener('submit', this.boundHandlePointerEvent, true);
          this.boundIframeDoc.defaultView.addEventListener(
            'scroll',
            this.boundHandleScroll,
            true,
          );
          this.boundIframeDoc.defaultView.addEventListener(
            'wheel',
            this.boundHandleWheel,
            { capture: true, passive: true },
          );
          this.boundIframeDoc.defaultView.addEventListener(
            'touchmove',
            this.boundHandleTouchMove,
            { capture: true, passive: true },
          );
        }
        if (this.isAddCommentMode()) {
          this.boundIframeDoc.documentElement.classList.add('rapider-comment-mode');
          if (this.boundIframeDoc.defaultView) {
            (this.boundIframeDoc.defaultView as any).__rapiderCommentModeActive = true;
          }
        }
        try {
          const style = this.boundIframeDoc.createElement('style');
          style.textContent = `
            html.rapider-comment-mode, html.rapider-comment-mode * {
              cursor: crosshair !important;
            }
            .comment-hover-target {
              outline: 2px dashed #ef4444 !important;
              outline-offset: -2px;
              cursor: crosshair !important;
            }
          `;
          this.boundIframeDoc.head?.appendChild(style);
        } catch {}
        this.recalculateBubblePositions();
      }
    }, 1000);
  }

  private removeIframeListeners(): void {
    if (this.boundIframeDoc) {
      this.boundIframeDoc.removeEventListener('click', this.boundHandleClick, true);
      this.boundIframeDoc.removeEventListener('mousedown', this.boundHandlePointerEvent, true);
      this.boundIframeDoc.removeEventListener('pointerdown', this.boundHandlePointerEvent, true);
      this.boundIframeDoc.removeEventListener('mouseup', this.boundHandlePointerEvent, true);
      this.boundIframeDoc.removeEventListener('pointerup', this.boundHandlePointerEvent, true);
      this.boundIframeDoc.removeEventListener('submit', this.boundHandlePointerEvent, true);
      this.boundIframeDoc.removeEventListener(
        'mousemove',
        this.boundHandleMouseMove,
        true,
      );
      this.boundIframeDoc.removeEventListener(
        'mouseout',
        this.boundHandleMouseOut,
        true,
      );
      this.boundIframeDoc.removeEventListener(
        'scroll',
        this.boundHandleScroll,
        true,
      );
      this.boundIframeDoc.removeEventListener(
        'wheel',
        this.boundHandleWheel,
        true,
      );
      this.boundIframeDoc.removeEventListener(
        'touchmove',
        this.boundHandleTouchMove,
        true,
      );
      if (this.boundIframeDoc.defaultView) {
        this.boundIframeDoc.defaultView.removeEventListener('click', this.boundHandleClick, true);
        this.boundIframeDoc.defaultView.removeEventListener('mousedown', this.boundHandlePointerEvent, true);
        this.boundIframeDoc.defaultView.removeEventListener('pointerdown', this.boundHandlePointerEvent, true);
        this.boundIframeDoc.defaultView.removeEventListener('mouseup', this.boundHandlePointerEvent, true);
        this.boundIframeDoc.defaultView.removeEventListener('pointerup', this.boundHandlePointerEvent, true);
        this.boundIframeDoc.defaultView.removeEventListener('submit', this.boundHandlePointerEvent, true);
        this.boundIframeDoc.defaultView.removeEventListener(
          'scroll',
          this.boundHandleScroll,
          true,
        );
        this.boundIframeDoc.defaultView.removeEventListener(
          'wheel',
          this.boundHandleWheel,
          true,
        );
        this.boundIframeDoc.defaultView.removeEventListener(
          'touchmove',
          this.boundHandleTouchMove,
          true,
        );
      }
      this.boundIframeDoc = null;
    }
  }

  private getLocalDoc(): Document | null {
    const iframe = this.captureZone?.nativeElement.querySelector(
      'iframe',
    ) as HTMLIFrameElement;
    if (iframe && iframe.contentDocument) {
      return iframe.contentDocument;
    }
    if (this.captureZone?.nativeElement) {
      return this.captureZone.nativeElement.ownerDocument;
    }
    return typeof document !== 'undefined' ? document : null;
  }

  public handleCaptureZoneMouseMove(event: MouseEvent): void {
    if (!this.isAddCommentMode()) return;
    const doc = this.getLocalDoc();
    if (!doc) return;
    let target = event.target as HTMLElement;
    if (
      target === doc.body ||
      target === doc.documentElement ||
      (this.canvasWrapper && target === this.canvasWrapper.nativeElement) ||
      (this.captureZone && target === this.captureZone.nativeElement)
    ) {
      return;
    }

    doc
      .querySelectorAll('.comment-hover-target')
      .forEach((el) => el.classList.remove('comment-hover-target'));
    target.classList.add('comment-hover-target');
  }

  public handleCaptureZoneMouseOut(event: MouseEvent): void {
    const doc = this.getLocalDoc();
    if (!doc) return;
    doc
      .querySelectorAll('.comment-hover-target')
      .forEach((el) => el.classList.remove('comment-hover-target'));
  }

  private generateUniqueSelector(el: HTMLElement): string {
    if (!el) return '';
    const path: string[] = [];
    let current: HTMLElement | null = el;
    while (current && current.nodeType === 1) {
      let selector = current.nodeName.toLowerCase();
      if (current.id) {
        selector += `#${current.id}`;
        path.unshift(selector);
        break;
      }

      let sibling = current.previousElementSibling;
      let nth = 1;
      while (sibling) {
        if (sibling.nodeName.toLowerCase() === selector) {
          nth++;
        }
        sibling = sibling.previousElementSibling;
      }
      selector += `:nth-of-type(${nth})`;
      path.unshift(selector);
      current = current.parentElement;
    }
    return path.join(' > ');
  }

  public handleCaptureZoneClick(event: MouseEvent): void {
    if (
      !this.isAddCommentMode() &&
      !(typeof window !== 'undefined' && (window as any).__rapiderCommentModeActive)
    ) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    const target = event.target as HTMLElement;
    let elementId = target?.getAttribute ? target.getAttribute('data-rapider-node-id') : null;
    if (!elementId && target?.setAttribute) {
      elementId = uuidv4();
      target.setAttribute('data-rapider-node-id', elementId);
    }

    const rect = target?.getBoundingClientRect
      ? target.getBoundingClientRect()
      : { left: event.clientX, top: event.clientY };
    const offsetX = Math.round(event.clientX - rect.left);
    const offsetY = Math.round(event.clientY - rect.top);

    this.pinnedMetadata.set({
      elementId: elementId || uuidv4(),
      uniqueSelector: target ? this.generateUniqueSelector(target) : '',
      offsetX: offsetX,
      offsetY: offsetY,
      absoluteX: Math.round(event.clientX),
      absoluteY: Math.round(event.clientY),
      pageId: this.activePageRoute() || this.activePageId() || '',
      tag: target?.tagName ? target.tagName.toLowerCase() : 'div',
    });

    this.isPinningFromModal = false;
    this.isCreateTaskModalVisible.set(true);
    // Keep comment mode flag active briefly to swallow trailing mouseup/click events
    setTimeout(() => {
      this.cancelAddCommentMode();
    }, 150);
  }

  public setCommentMode(active: boolean): void {
    this.isAddCommentMode.set(active);
    if (typeof window !== 'undefined') {
      (window as any).__rapiderCommentModeActive = active;
    }
    const doc = this.getLocalDoc();
    if (doc) {
      if (active) {
        doc.documentElement.classList.add('rapider-comment-mode');
        if (doc.defaultView) {
          (doc.defaultView as any).__rapiderCommentModeActive = true;
        }
      } else {
        doc.documentElement.classList.remove('rapider-comment-mode');
        doc
          .querySelectorAll('.comment-hover-target')
          .forEach((el) => el.classList.remove('comment-hover-target'));
        if (doc.defaultView) {
          (doc.defaultView as any).__rapiderCommentModeActive = false;
        }
      }
    }
  }

  public startReportBug(): void {
    this.draftTaskType.set('Bug');
    this.draftTaskTitle.set('');
    this.draftTaskDescription.set('');
    this.pinnedMetadata.set(null);
    this.isPinningFromModal = false;
    this.setCommentMode(true);
  }

  public openRequestFeatureModal(): void {
    this.draftTaskType.set('Feature');
    this.draftTaskTitle.set('');
    this.draftTaskDescription.set('');
    this.pinnedMetadata.set(null);
    this.isPinningFromModal = false;
    this.isCreateTaskModalVisible.set(true);
  }

  public startFeatureBubblePin(): void {
    this.isPinningFromModal = true;
    this.isCreateTaskModalVisible.set(false);
    this.setCommentMode(true);
  }

  public cancelAddCommentMode(): void {
    this.setCommentMode(false);
    if (this.isPinningFromModal) {
      this.isPinningFromModal = false;
      this.isCreateTaskModalVisible.set(true);
    }
  }

  public openCreateTaskModal(type: 'Bug' | 'Feature' = 'Bug'): void {
    this.draftTaskType.set(type);
    this.draftTaskTitle.set('');
    this.draftTaskDescription.set('');
    this.pinnedMetadata.set(null);
    this.isPinningFromModal = false;
    this.isCreateTaskModalVisible.set(true);
  }

  public closeCreateTaskModal(): void {
    this.isPinningFromModal = false;
    this.isCreateTaskModalVisible.set(false);
    this.setCommentMode(false);
    this.draftTaskTitle.set('');
    this.draftTaskDescription.set('');
    this.pinnedMetadata.set(null);
  }

  /**
   * Appends formatted markdown snippets (table, bold, code block, list) into the draft task description.
   *
   * @param {string} format The snippet key to append.
   */
  public insertDraftMarkdown(format: string): void {
    const current = this.draftTaskDescription();
    let addition = '';
    switch (format) {
      case 'table':
        addition =
          '\n| Key | Summary | Severity | Status |\n|---|---|---|---|\n| TSK-01 | Issue detail goes here | High | Open |\n| TSK-02 | Additional note | Medium | Pending |\n';
        break;
      case 'bold':
        addition = '**bold text**';
        break;
      case 'italic':
        addition = '*italic text*';
        break;
      case 'code':
        addition = '\n```javascript\n// code snippet here\n```\n';
        break;
      case 'list':
        addition = '\n- Item 1\n- Item 2\n- Item 3\n';
        break;
      case 'quote':
        addition = '\n> Quoted note or comment\n';
        break;
    }
    this.draftTaskDescription.set(
      current ? `${current}\n${addition}` : addition,
    );
  }

  public async handleCreateTaskSubmit(): Promise<void> {
    const appId = this.currentAppId();
    const title = this.draftTaskTitle().trim();
    const description = this.draftTaskDescription().trim();
    const type = this.draftTaskType();
    const pin = this.pinnedMetadata();

    if (!appId || !title) return;

    this.isSubmittingTask.set(true);

    try {
      const user = this.authStore.user();
      const content = description ? `${title}\n\n${description}` : title;
      const targetAppId =
        this.currentAppDef()?.applicationDefinitionId ||
        this.currentAppDef()?.id ||
        appId;
      const activePage = this.activePageRoute() || this.activePageId() || '';
      const metadata: Array<{ key: string; value: string }> = [
        { key: 'title', value: title },
        ...(description ? [{ key: 'description', value: description }] : []),
        { key: 'pageId', value: activePage },
        { key: 'appId', value: targetAppId },
      ];
      if (pin) {
        if (pin.elementId) metadata.push({ key: 'elementId', value: pin.elementId });
        if (pin.uniqueSelector) metadata.push({ key: 'uniqueSelector', value: pin.uniqueSelector });
        if (pin.offsetX !== undefined) metadata.push({ key: 'offsetX', value: pin.offsetX.toString() });
        if (pin.offsetY !== undefined) metadata.push({ key: 'offsetY', value: pin.offsetY.toString() });
        if (pin.absoluteX !== undefined) metadata.push({ key: 'absoluteX', value: pin.absoluteX.toString() });
        if (pin.absoluteY !== undefined) metadata.push({ key: 'absoluteY', value: pin.absoluteY.toString() });
        if (pin.tag) metadata.push({ key: 'tag', value: pin.tag });
      }

      const category = type === 'Feature' ? 'Feature Request' : 'Bug Report';

      const newComment: Omit<Comment, 'id'> = {
        content: content,
        category: category,
        status: 'Open',
        projectId: (user as any)?.['lastProjectId'] || '',
        relatedEntityName: 'App',
        relatedEntityId: targetAppId,
        metadata: metadata,
      };

      const created = await firstValueFrom(
        this.commentController.create({ body: newComment as any }),
      );
      this.comments.update((c) => [created, ...c]);
      this.notificationService.createNotification(
        'success',
        type === 'Feature' ? 'Feature Requested' : 'Bug Reported',
        `Successfully logged ${type === 'Feature' ? 'feature request' : 'bug'} "${title.slice(0, 25)}..."`,
      );

      this.isPinningFromModal = false;
      this.isCreateTaskModalVisible.set(false);
      this.draftTaskTitle.set('');
      this.draftTaskDescription.set('');
      this.pinnedMetadata.set(null);
      this.fetchTasks();
      setTimeout(() => this.recalculateBubblePositions(), 300);
    } catch (err) {
      console.error('Failed to create task:', err);
      this.notificationService.createNotification(
        'error',
        'Error',
        'Failed to create task.',
      );
    } finally {
      this.isSubmittingTask.set(false);
    }
  }

  public recalculateBubblePositions(): void {
    const doc = this.getLocalDoc();
    const container =
      this.canvasWrapper?.nativeElement || this.captureZone?.nativeElement;
    if (!doc || !container) return;

    const canvasRect = container.getBoundingClientRect();

    let iframeTopOffset = 0;
    let iframeLeftOffset = 0;
    const iframe = this.captureZone?.nativeElement.querySelector(
      'iframe',
    ) as HTMLIFrameElement;
    if (iframe && doc === iframe.contentDocument) {
      const iframeRect = iframe.getBoundingClientRect();
      iframeTopOffset = iframeRect.top;
      iframeLeftOffset = iframeRect.left;
    }

    const newPositions: Record<
      string,
      { top: number; left: number; isOrphaned: boolean }
    > = {};

    for (const task of this.unifiedTasks()) {
      if (!this.isTaskOnActivePage(task)) {
        continue;
      }
      const meta = this.parseTaskMetadata(task);
      if (!meta) continue;

      const elementId = meta.elementId;
      let el: HTMLElement | null = null;

      if (elementId) {
        el = doc.querySelector(
          `[data-rapider-node-id="${elementId}"]`,
        ) as HTMLElement;
      }

      if (!el && meta.uniqueSelector) {
        try {
          el = doc.querySelector(meta.uniqueSelector) as HTMLElement;
          if (el && elementId) {
            el.setAttribute('data-rapider-node-id', elementId);
          }
        } catch {}
      }

      if (el) {
        const rect = el.getBoundingClientRect();
        if (rect.width === 0 && rect.height === 0) continue;

        const offsetY = parseFloat(meta.offsetY ?? meta.y ?? meta.top ?? '0');
        const offsetX = parseFloat(meta.offsetX ?? meta.x ?? meta.left ?? '0');

        const top = Math.round(rect.top + iframeTopOffset + offsetY - canvasRect.top);
        const left = Math.round(rect.left + iframeLeftOffset + offsetX - canvasRect.left);

        // Omit bubbles whose target elements have scrolled out of the visible canvas boundaries
        if (!isCoordinateWithinCanvasBounds(top, left, canvasRect.width, canvasRect.height)) {
          continue;
        }

        newPositions[task.rawId] = {
          top,
          left,
          isOrphaned: false,
        };
      } else {
        const absX = parseFloat(meta.absoluteX ?? meta.x ?? meta.left);
        const absY = parseFloat(meta.absoluteY ?? meta.y ?? meta.top);
        if (!isNaN(absX) && !isNaN(absY)) {
          const top = Math.round(absY + iframeTopOffset - canvasRect.top);
          const left = Math.round(absX + iframeLeftOffset - canvasRect.left);

          if (!isCoordinateWithinCanvasBounds(top, left, canvasRect.width, canvasRect.height)) {
            continue;
          }

          newPositions[task.rawId] = {
            top,
            left,
            isOrphaned: true,
          };
        }
      }
    }

    this.bubblePositions.set(newPositions);
  }

  // Task Selection, Details & Delete Actions
  public selectTask(task: UnifiedTaskItem, e?: Event): void {
    if (e) e.stopPropagation();
    this.selectedTaskId.set(task.rawId);

    const cardEl = document.getElementById(`task-card-${task.rawId}`);
    if (cardEl) {
      cardEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }

  public onBubbleClick(task: UnifiedTaskItem, e: MouseEvent): void {
    e.stopPropagation();
    if (this.selectedTaskId() === task.rawId) {
      this.openTaskDetails(task);
    } else {
      this.selectTask(task);
    }
  }

  public expandedTaskIds = signal<Set<string>>(new Set());

  public isTaskDescriptionExpanded(taskId: string): boolean {
    return this.expandedTaskIds().has(taskId);
  }

  public toggleTaskDescription(task: UnifiedTaskItem, e?: Event): void {
    if (e) e.stopPropagation();
    const current = new Set(this.expandedTaskIds());
    if (current.has(task.rawId)) {
      current.delete(task.rawId);
    } else {
      current.add(task.rawId);
    }
    this.expandedTaskIds.set(current);
  }

  public isDescriptionLong(description?: string): boolean {
    if (!description) return false;
    const lines = description.trim().split(/\r\n|\r|\n/);
    if (lines.length > 5) return true;
    if (description.trim().length > 240) return true;
    return false;
  }

  public openTaskDetails(task: UnifiedTaskItem): void {
    this.selectedTaskId.set(task.rawId);
    this.selectedTaskForDetail.set(task);
    this.triageNote.set('');
    this.isEditingTaskDetail.set(false);
    this.loadTaskReplies(task.rawId);
  }

  public closeTaskDetailDrawer(): void {
    this.selectedTaskForDetail.set(null);
    this.taskReplies.set([]);
    this.replyContent.set('');
    this.triageNote.set('');
    this.isEditingTaskDetail.set(false);
  }

  /** Optional note entered by Owner/Admin when closing or triaging a task in 1 click */
  public triageNote = signal<string>('');

  /**
   * RPDR-613: Reporter can edit Title, Description, and Type (Bug / Feature) while task status is Open.
   */
  public canEditTask(task: UnifiedTaskItem | null): boolean {
    if (!task) return false;
    if (task.isApproved === true) return false;
    if (this.getNormalizedTaskStatus(task) !== 'Open') return false;
    return this.isTaskReporter(task);
  }

  public startEditingTask(task: UnifiedTaskItem, e?: Event): void {
    if (e) e.stopPropagation();
    if (!this.canEditTask(task)) return;
    if (this.selectedTaskForDetail()?.rawId !== task.rawId) {
      this.openTaskDetails(task);
    }
    this.editTaskTitle.set(task.title || '');
    this.editTaskDescription.set(task.description || '');
    this.editTaskType.set(task.type === 'Feature' ? 'Feature' : 'Bug');
    this.isEditingTaskDetail.set(true);
  }

  public cancelEditingTask(): void {
    this.isEditingTaskDetail.set(false);
  }

  public async saveTaskEdit(task: UnifiedTaskItem): Promise<void> {
    if (!task || !this.canEditTask(task)) return;
    const title = this.editTaskTitle().trim();
    const description = this.editTaskDescription().trim();
    const newType = this.editTaskType();
    if (!title) return;

    this.isSavingTaskEdit.set(true);
    try {
      const newCategory = newType === 'Feature' ? 'Feature Request' : 'Bug Report';
      const newContent = description ? `${title}\n\n${description}` : title;
      const raw = (task.rawItem as any) || {};
      const existingMetaArr: Array<{ key: string; value: any }> = Array.isArray(raw.metadata)
        ? raw.metadata.filter((m: any) => m && m.key && m.key !== 'title' && m.key !== 'description')
        : [];
      const updatedMetadata: Array<{ key: string; value: any }> = [
        { key: 'title', value: title },
        ...(description ? [{ key: 'description', value: description }] : []),
        ...existingMetaArr,
      ];

      let savedContent = newContent;
      try {
        await firstValueFrom(
          this.commentController.updateById({
            id: task.rawId,
            body: {
              content: newContent,
              category: newCategory,
              metadata: updatedMetadata,
            } as any,
          }),
        );
      } catch (primaryErr: any) {
        if (primaryErr?.status === 422) {
          savedContent = `[${newType}] ${newContent}`;
          await firstValueFrom(
            this.commentController.updateById({
              id: task.rawId,
              body: {
                content: savedContent,
              } as any,
            }),
          );
        } else {
          throw primaryErr;
        }
      }

      this.comments.update((list) =>
        list.map((c) =>
          c.id === task.rawId
            ? ({
                ...c,
                content: savedContent,
                category: newCategory as any,
                metadata: updatedMetadata as any,
              } as Comment)
            : c,
        ),
      );

      if (this.selectedTaskForDetail()?.rawId === task.rawId) {
        this.selectedTaskForDetail.update((curr) =>
          curr
            ? {
                ...curr,
                title,
                description,
                type: newType,
                rawItem: {
                  ...(curr.rawItem || {}),
                  content: newContent,
                  category: newCategory,
                  metadata: updatedMetadata,
                },
              }
            : null,
        );
      }

      this.isEditingTaskDetail.set(false);
      this.notificationService.createNotification(
        'success',
        'Task Updated',
        `Task ${task.id} has been updated.`,
      );
    } catch (err: any) {
      console.error('[DynamicAppViewer] Failed to update task:', err);
      this.notificationService.createNotification(
        'error',
        'Update Failed',
        err?.error?.error?.message || err?.message || 'Failed to save task changes.',
      );
    } finally {
      this.isSavingTaskEdit.set(false);
    }
  }

  public toggleReportedByMeFilter(e?: Event): void {
    if (e) e.stopPropagation();
    this.reportedByMeOnly.update((v) => !v);
    this.loadedTasksLimit.set(20);
  }

  public clearReportedByMeFilter(): void {
    this.reportedByMeOnly.set(false);
    this.loadedTasksLimit.set(20);
  }

  public selectAllStatusesFilter(e?: Event): void {
    if (e) e.stopPropagation();
    this.selectedTaskStatuses.set([]);
    this.taskStatusFilter.set('ALL');
    this.loadedTasksLimit.set(20);
  }

  public toggleTaskStatusCheckbox(status: string, e?: Event): void {
    if (e) e.stopPropagation();
    this.selectedTaskStatuses.update((curr) =>
      curr.includes(status) ? curr.filter((s) => s !== status) : [...curr, status],
    );
    this.taskStatusFilter.set('ALL');
    this.loadedTasksLimit.set(20);
  }

  public removeTaskStatusFilter(status: string): void {
    this.selectedTaskStatuses.update((curr) => curr.filter((s) => s !== status));
    if (this.taskStatusFilter() === status) {
      this.taskStatusFilter.set('ALL');
    }
    this.loadedTasksLimit.set(20);
  }

  /**
   * Sets or toggles the active task type filter ('ALL', 'Bug', 'Feature').
   * If the clicked type is already active, resets back to 'ALL'.
   *
   * @param type - The selected task type.
   */
  public setTaskTypeFilter(type: string): void {
    this.taskTypeFilter.set(toggleFilterValue(this.taskTypeFilter(), type));
    this.loadedTasksLimit.set(20);
  }

  /**
   * Sets or toggles the active task status filter ('ALL', 'Open', 'In Progress', 'Resolved', 'Closed').
   * If the clicked status is already active, resets back to 'ALL'.
   *
   * @param status - The selected task status.
   */
  public setTaskStatusFilter(status: string): void {
    this.selectedTaskStatuses.set([]);
    this.taskStatusFilter.set(toggleFilterValue(this.taskStatusFilter(), status));
    this.loadedTasksLimit.set(20);
  }

  /**
   * Clears the current task search query.
   */
  public clearSearchFilter(): void {
    this.taskSearchQuery.set('');
    this.loadedTasksLimit.set(20);
  }

  /**
   * Clears the task type filter, resetting it back to 'ALL'.
   */
  public clearTypeFilter(): void {
    this.taskTypeFilter.set('ALL');
    this.loadedTasksLimit.set(20);
  }

  /**
   * Clears the task status filter, resetting it back to 'ALL'.
   */
  public clearStatusFilter(): void {
    this.selectedTaskStatuses.set([]);
    this.taskStatusFilter.set('ALL');
    this.loadedTasksLimit.set(20);
  }

  /**
   * Resets all task filters (search, type, status, reportedByMe, and page) back to their default states.
   */
  public resetAllFilters(): void {
    this.taskSearchQuery.set('');
    this.taskTypeFilter.set('ALL');
    this.taskStatusFilter.set('ALL');
    this.selectedTaskStatuses.set([]);
    this.reportedByMeOnly.set(false);
    this.taskApprovalFilter.set('ALL');
    this.taskPageFilter.set('CURRENT');
    this.loadedTasksLimit.set(20);
  }

  public confirmDeleteTask(task: UnifiedTaskItem, e?: Event): void {
    if (e) e.stopPropagation();
    this.taskToDelete.set(task);
  }

  public async executeDeleteTask(): Promise<void> {
    const task = this.taskToDelete();
    if (!task) return;

    const appId = this.currentAppId();

    try {
      if (
        task.type === 'Bug' ||
        (task.rawItem as any)?.category ||
        (task.rawItem as any)?.relatedEntityName === 'App'
      ) {
        await firstValueFrom(
          this.commentController.deleteById({ id: task.rawId }),
        );
        this.comments.update((list) =>
          list.filter((c) => c.id !== task.rawId),
        );
        this.totalCommentsCount.update((count) =>
          count !== null && count > 0 ? count - 1 : null,
        );
      } else {
        if (appId) {
          try {
            await firstValueFrom(
              this.definitionController.deleteById({ id: task.rawId }),
            );
          } catch {
            await firstValueFrom(
              this.definitionController.updateAppFeatureRequestStatus({
                id: appId,
                requestId: task.rawId,
                body: { status: 'Declined' },
              }),
            );
          }
          this.featureRequests.update((list) =>
            list.filter((r) => r.id !== task.rawId),
          );
        }
      }

      this.notificationService.createNotification(
        'success',
        'Task Deleted',
        `Task ${task.id} was deleted successfully.`,
      );

      if (this.selectedTaskId() === task.rawId) {
        this.selectedTaskId.set(null);
      }
      if (this.selectedTaskForDetail()?.rawId === task.rawId) {
        this.selectedTaskForDetail.set(null);
      }
    } catch (err) {
      console.error('Failed to delete task:', err);
      this.notificationService.createNotification(
        'error',
        'Error',
        'Failed to delete task.',
      );
    } finally {
      this.taskToDelete.set(null);
    }
  }

  public formatTaskDate(dateStr: string | Date | undefined): string {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  }

  /**
   * Determines if the current user can approve/reject app tasks (bugs and feature requests).
   * - App Owner skips permission requirement and can always approve.
   * - Admins ('admin', 'Admin', 'SuperAdmin') can always approve.
   * - Users with explicit 'app:approve-request' permission can approve.
   */
  public canApproveTasks = computed(() => {
    const person = this.authStore.activePerson();
    const user = this.authStore.user();
    if (!person && !user) return false;

    const currentPersonId = person?.id;
    const currentUserId = user?.id;
    const appDef = this.currentAppDef();
    const appCreatedById =
      appDef?.createdById ||
      appDef?.createdByPersonId ||
      appDef?.createdBy ||
      appDef?.value?.createdById ||
      appDef?.value?.createdByPersonId ||
      appDef?.value?.createdBy;

    // 1. App Owner bypass: app owner can always approve
    if (appCreatedById && (appCreatedById === currentPersonId || appCreatedById === currentUserId)) {
      return true;
    }

    // 2. Admin & Specific Permission check
    const storePermissions: string[] = this.authStore.permissions() || [];
    const userPermissions: string[] = (user as any)?.permissions || [];
    const userScope: string[] = (user as any)?.scope || [];
    const userRoles = (user as any)?.roles || [];

    const allAuthStrings = [
      ...storePermissions,
      ...userPermissions,
      ...userScope,
      ...(Array.isArray(userRoles) ? userRoles.map((r: any) => (typeof r === 'string' ? r : r?.name || '')) : []),
    ].map((s) => (typeof s === 'string' ? s.trim().toLowerCase() : ''));

    const isAdmin =
      allAuthStrings.includes('admin') ||
      allAuthStrings.includes('superadmin') ||
      allAuthStrings.includes('administrator');

    const hasApprovePermission =
      allAuthStrings.includes('app:approve-request') ||
      allAuthStrings.includes('approveapprequest') ||
      isAdmin;

    return hasApprovePermission;
  });

  /**
   * Checks whether the current user is the original creator / reporter of this task.
   */
  public isTaskReporter(task: UnifiedTaskItem | null): boolean {
    if (!task) return false;
    const person = this.authStore.activePerson();
    const user = this.authStore.user();
    const currentPersonId = person?.id;
    const currentUserId = (user as any)?.id;
    const currentEmail = ((user as any)?.email || (user as any)?.username || '').toLowerCase();
    const currentName = (
      person?.firstName
        ? `${person.firstName} ${person.lastName || ''}`.trim()
        : (user as any)?.username || (user as any)?.name || ''
    ).toLowerCase();

    const raw = task.rawItem as any;
    const taskCreatorId =
      task.createdById ||
      raw?.createdById ||
      raw?.createdByPersonId ||
      raw?.userId ||
      raw?.authorId;

    if (taskCreatorId && (taskCreatorId === currentPersonId || taskCreatorId === currentUserId)) {
      return true;
    }
    if (task.createdBy && currentName && task.createdBy.toLowerCase() === currentName) {
      return true;
    }
    if (task.createdBy && currentEmail && task.createdBy.toLowerCase() === currentEmail) {
      return true;
    }
    return false;
  }

  /**
   * Determines if the current user can modify approval (approve, reject, reset).
   * Rules 2 & 5:
   * - After a task is approved and status is not open any more, user cannot un-approve or reject it.
   * - Only owner and admin can reset after approved IF the status is open.
   */
  public canModifyApproval(task: UnifiedTaskItem | null): boolean {
    if (!task) return false;
    if (!this.canApproveTasks()) return false;
    const norm = this.getNormalizedTaskStatus(task);
    // Triage actions apply in Open, Reopened, Accepted (pre-execution queue), and Closed states
    return norm === 'Open' || norm === 'Reopened' || norm === 'Accepted' || norm === 'Closed';
  }

  /**
   * Navigates to the App Builder Preview Harness for the active app and task page.
   * Strictly restricted to App Owners and Administrators.
   */
  public openInPreviewHarness(task?: UnifiedTaskItem | null): void {
    if (!this.canApproveTasks()) {
      this.notificationService.createNotification(
        'error',
        'Permission Denied',
        'Only the application owner or an administrator can open the App Builder Preview Harness.',
      );
      return;
    }
    const appId = this.currentAppId();
    if (!appId) return;
    const pageRoute = task?.pageId || this.activePageRoute() || this.activePageId() || '';
    if (pageRoute) {
      this.router.navigate(['/app-builder', appId, 'preview', pageRoute]);
    } else {
      this.router.navigate(['/app-builder', appId, 'preview']);
    }
  }

  /**
   * Checks if current user can mark the task as Resolved:
   * Rule: Resolution verification is strictly disabled in the installed application viewer.
   * Tasks can only be tested and resolved in the App Builder Preview Harness with the develop build.
   */
  public canMarkResolved(task: UnifiedTaskItem | null): boolean {
    return false;
  }

  /**
   * Checks if current user can mark the task as Not Resolved:
   * Rule: Resolution verification is strictly disabled in the installed application viewer.
   * Tasks can only be tested and rejected in the App Builder Preview Harness with the develop build.
   */
  public canMarkNotResolved(task: UnifiedTaskItem | null): boolean {
    return false;
  }

  /**
   * Sets the approval state of an app task (true = approved, false = rejected, null = pending).
   * Only App Owners and Admins (users with 'app:approve-request' or admin roles) are authorized.
   */
  public async setTaskApproval(task: UnifiedTaskItem, approved: boolean | null, e?: Event): Promise<void> {
    if (e) e.stopPropagation();
    if (!this.canApproveTasks()) {
      this.notificationService.createNotification(
        'error',
        'Permission Denied',
        'Only application owners and authorized administrators can approve or reject requests.'
      );
      return;
    }

    // Once approved and actively executing or beyond (not Open / Accepted), cannot un-approve or close
    const currentNorm = this.getNormalizedTaskStatus(task);
    if (task.isApproved === true && approved !== true) {
      if (currentNorm !== 'Open' && currentNorm !== 'Accepted') {
        this.notificationService.createNotification(
          'warning',
          'Action Not Allowed',
          'This task is in progress or completed. Approval cannot be revoked once active.'
        );
        return;
      }
    }

    try {
      let updatedStatus = task.status;
      if (approved === true) {
        updatedStatus = 'Accepted';
      } else if (approved === false) {
        updatedStatus = 'Closed';
      } else if (approved === null) {
        updatedStatus = 'Open';
      }

      const updatePayload: any = { isApproved: approved };
      if (updatedStatus !== task.status) {
        updatePayload.status = updatedStatus;
      }

      if (
        task.type === 'Bug' ||
        (task.rawItem as any)?.category ||
        (task.rawItem as any)?.relatedEntityName === 'App'
      ) {
        await firstValueFrom(
          this.commentController.updateById({
            id: task.rawId,
            body: updatePayload,
          })
        );

        // If Owner/Admin entered an optional triage note in Task Detail, post it automatically as a reply
        const optionalNote =
          this.selectedTaskForDetail()?.rawId === task.rawId
            ? this.triageNote().trim()
            : '';
        if (optionalNote) {
          try {
            const actionLabel =
              approved === false
                ? '**Task Closed:**'
                : approved === true
                  ? '**Task Approved:**'
                  : '**Task Reopened:**';
            const createdReply = await firstValueFrom(
              this.commentController.reply({
                parentCommentId: task.rawId,
                body: { content: `${actionLabel} ${optionalNote}` },
              }),
            );
            this.taskReplies.update((list) => [...list, createdReply]);
            this.triageNote.set('');
          } catch (replyErr) {
            console.warn('[DynamicAppViewer] Optional triage note failed to post:', replyErr);
          }
        }
      }

      // Update local task reference
      task.isApproved = approved;
      task.status = updatedStatus;

      // Update in comments signal
      this.comments.update((list) =>
        list.map((c) => {
          if (c.id === task.rawId) {
            return { ...c, isApproved: approved, status: updatedStatus as any };
          }
          return c;
        })
      );

      // Keep detail view in sync
      if (this.selectedTaskForDetail()?.rawId === task.rawId) {
        this.selectedTaskForDetail.update((current) => (current ? { ...current, isApproved: approved, status: updatedStatus } : null));
      }

      let title = 'Status Updated';
      let message = 'Approval status updated successfully.';
      let type: 'success' | 'warning' | 'info' = 'info';

      if (approved === true) {
        title = 'Task Approved';
        message = 'This request is now approved and eligible for automated AI execution.';
        type = 'success';
      } else if (approved === false) {
        title = 'Task Closed';
        message = 'This task has been closed.';
        type = 'info';
      } else {
        title = 'Task Reopened';
        message = 'This task has been reopened and returned to Open status.';
        type = 'info';
      }

      this.notificationService.createNotification(type, title, message);
    } catch (err: any) {
      console.error('Failed to update task approval:', err);
      this.notificationService.createNotification(
        'error',
        'Approval Failed',
        err.message || 'Could not update approval status.'
      );
    }
  }

  /**
   * Opens the Edit Approval Modal for a task with Open status.
   */
  public openEditApprovalModal(task: UnifiedTaskItem, e?: Event): void {
    if (e) e.stopPropagation();
    if (task.status !== 'Open' && task.status !== 'open' && task.status !== 'Approved' && task.status !== 'Closed') {
      this.notificationService.createNotification(
        'warning',
        'Cannot Edit Approval',
        'Only tasks in initial open/approval status can have their approval status modified.'
      );
      return;
    }
    const val: 'approved' | 'rejected' = task.isApproved === false ? 'rejected' : 'approved';
    this.editingApprovalTask.set(task);
    this.editingApprovalValue.set(val);
    this.editingApprovalInitialValue.set(val);
    this.isEditApprovalModalVisible.set(true);
  }

  /**
   * Closes the Edit Approval Modal.
   */
  public closeEditApprovalModal(): void {
    this.isEditApprovalModalVisible.set(false);
    this.editingApprovalTask.set(null);
  }

  /**
   * Confirms the change in the Edit Approval Modal.
   * If the chosen value is the same as the initial value, does NOT patch data.
   * If different, updates task approval.
   */
  public async confirmEditApproval(): Promise<void> {
    const task = this.editingApprovalTask();
    if (!task) {
      this.closeEditApprovalModal();
      return;
    }

    const currentVal = this.editingApprovalValue();
    const initialVal = this.editingApprovalInitialValue();

    // If same value, do not patch backend data
    if (currentVal === initialVal) {
      this.closeEditApprovalModal();
      return;
    }

    const newApproved = currentVal === 'approved';
    await this.setTaskApproval(task, newApproved);
    this.closeEditApprovalModal();
  }

  /**
   * User marks task as Resolved:
   * Only allowed in the App Builder Preview Harness with develop build.
   */
  public async handleMarkResolved(task: UnifiedTaskItem, e?: Event): Promise<void> {
    if (e) e.stopPropagation();
    this.notificationService.createNotification(
      'info',
      'Preview Harness Required',
      'Task resolution verification can only be performed in the App Builder Preview Harness with the latest develop build.'
    );
    this.openInPreviewHarness(task);
  }

  /**
   * Opens the reopen/rejection reason modal:
   * - When a task is in 'Released' status and the current user is the Reporter (or App Owner/Admin),
   *   opens the Reopen Reason modal directly so the user can explain why the issue persists and move it to 'Reopened'.
   * - For 'In Review' QA verification (Owner/Admin only), directs to the App Builder Preview Harness.
   */
  public openRejectModal(task: UnifiedTaskItem, e?: Event): void {
    if (e) e.stopPropagation();
    const normStatus = this.getNormalizedTaskStatus(task);
    if (task.status === 'Released' || normStatus === 'Released') {
      if (!this.isTaskReporter(task) && !this.canApproveTasks()) {
        this.notificationService.createNotification(
          'error',
          'Permission Denied',
          'Only the task reporter, application owner, or administrator can reopen a released task.',
        );
        return;
      }
      this.taskPendingRejection.set(task);
      this.rejectionReason.set('');
      this.isRejectModalVisible.set(true);
      return;
    }

    if (!this.canApproveTasks()) {
      this.notificationService.createNotification(
        'error',
        'Permission Denied',
        'Only the application owner or an administrator can perform QA verification.',
      );
      return;
    }

    this.notificationService.createNotification(
      'info',
      'Preview Harness Required',
      'Task resolution verification can only be performed in the App Builder Preview Harness with the latest develop build.'
    );
    this.openInPreviewHarness(task);
  }

  public closeRejectModal(): void {
    this.isRejectModalVisible.set(false);
    this.taskPendingRejection.set(null);
    this.rejectionReason.set('');
  }

  /**
   * Confirms task reopen (from 'Released' -> 'Reopened') or solution rejection ('In Review' -> 'Open'):
   * 1. Saves user feedback as a structured comment reply.
   * 2. Updates task status to 'Reopened' (for Released tasks) or 'Open' and resets isApproved to null.
   * 3. Requires App Owner or Admin approval before autonomous AI re-execution.
   */
  public async confirmRejectSolution(): Promise<void> {
    const task = this.taskPendingRejection();
    const reason = this.rejectionReason().trim();
    if (!task || !task.rawId || !reason) return;

    const isReleasedReopen =
      task.status === 'Released' || this.getNormalizedTaskStatus(task) === 'Released';
    const targetStatus = isReleasedReopen ? 'Reopened' : 'Open';
    this.isSubmittingRejection.set(true);
    const formattedReply = isReleasedReopen
      ? `Task reopened by reporter with the reason below:\n\n${reason}`
      : `Solution is rejected with the reason below:\n\n${reason}`;

    try {
      // 1. Post reopen/rejection reason as reply
      const createdReply = await firstValueFrom(
        this.commentController.reply({
          parentCommentId: task.rawId,
          body: { content: formattedReply },
        })
      );

      if (this.selectedTaskForDetail()?.rawId === task.rawId) {
        this.taskReplies.update((list) => [...list, createdReply]);
      }

      // 2. Update task status to 'Reopened' (or 'Open') and reset approval to null
      if (
        task.type === 'Bug' ||
        (task.rawItem as any)?.category ||
        (task.rawItem as any)?.relatedEntityName === 'App'
      ) {
        try {
          await firstValueFrom(
            this.commentController.updateById({
              id: task.rawId,
              body: { status: targetStatus as any, isApproved: null } as any,
            })
          );
        } catch (patchErr: any) {
          // Fallback if server has not yet deployed reporter isApproved=null allowance
          if (patchErr?.status === 403) {
            await firstValueFrom(
              this.commentController.updateById({
                id: task.rawId,
                body: { status: targetStatus as any } as any,
              })
            );
          } else {
            throw patchErr;
          }
        }
      } else {
        const appId = this.currentAppId();
        if (appId) {
          await firstValueFrom(
            this.definitionController.updateAppFeatureRequestStatus({
              id: appId,
              requestId: task.rawId,
              body: { status: targetStatus as any },
            })
          );
        }
      }

      // 3. Optimistic local state update
      task.status = targetStatus;
      task.isApproved = null;

      this.comments.update((list) =>
        list.map((c) => {
          if (c.id === task.rawId) {
            return { ...c, status: targetStatus as any, isApproved: null };
          }
          return c;
        })
      );

      if (this.selectedTaskForDetail()?.rawId === task.rawId) {
        this.selectedTaskForDetail.update((current) =>
          current ? { ...current, status: targetStatus, isApproved: null } : null
        );
      }

      this.closeRejectModal();

      this.notificationService.createNotification(
        'info',
        isReleasedReopen ? 'Task Reopened' : 'Solution Rejected',
        isReleasedReopen
          ? 'Reopen reason recorded and task moved to Reopened. Requires Owner/Admin approval before re-execution.'
          : 'Rejection reason recorded and task reopened. Requires Owner/Admin approval before re-execution.'
      );
    } catch (err: any) {
      console.error('[DynamicAppViewer] Failed to reopen/reject task:', err);
      this.notificationService.createNotification(
        'error',
        isReleasedReopen ? 'Reopen Failed' : 'Rejection Failed',
        err.message || 'Could not record reopen reason.'
      );
    } finally {
      this.isSubmittingRejection.set(false);
    }
  }

  /**
   * User marks task as Not Resolved (delegates to openRejectModal to capture explanation)
   */
  public async handleMarkNotResolved(task: UnifiedTaskItem, e?: Event): Promise<void> {
    this.openRejectModal(task, e);
  }

  /**
   * Toggles the approval state of an app task (isApproved: true / false).
   */
  public async toggleTaskApproval(task: UnifiedTaskItem, e?: Event): Promise<void> {
    const next = task.isApproved === true ? false : true;
    return this.setTaskApproval(task, next, e);
  }

  /**
   * Fetches child replies for the active task in the details drawer.
   */
  public loadTaskReplies(taskId: string): void {
    if (!taskId) {
      this.taskReplies.set([]);
      return;
    }
    this.isLoadingReplies.set(true);
    this.commentController
      .find({
        filter: {
          where: { parentCommentId: taskId },
          order: ['createdDate ASC'],
        },
      })
      .subscribe({
        next: (replies) => {
          this.taskReplies.set(replies || []);
          this.isLoadingReplies.set(false);
        },
        error: (err) => {
          console.error('[DynamicAppViewer] Failed to load task replies:', err);
          this.taskReplies.set([]);
          this.isLoadingReplies.set(false);
        },
      });
  }

  /**
   * Posts a comment reply on the currently selected task.
   */
  public async submitTaskReply(): Promise<void> {
    const text = this.replyContent().trim();
    const task = this.selectedTaskForDetail();
    if (!text || !task || !task.rawId) return;

    this.isSubmittingReply.set(true);
    try {
      const createdReply = await firstValueFrom(
        this.commentController.reply({
          parentCommentId: task.rawId,
          body: { content: text },
        })
      );
      this.taskReplies.update((list) => [...list, createdReply]);
      // Optimistically update parent comment's reply stats in the local comments list
      const activeP = this.authStore.activePerson() as any;
      const activeU = this.authStore.user() as any;
      const authorName = activeP?.fullName || activeU?.firstName || 'User';
      this.comments.update((items) =>
        items.map((c) => {
          if (c.id === task.rawId) {
            const currentCount = ((c as any).replyCount ?? (c as any).repliesCount ?? 0) + 1;
            const existingNames = [...((c as any).lastReplierNames ?? (c as any).lastRepliers ?? [])];
            if (!existingNames.includes(authorName)) {
              existingNames.push(authorName);
            }
            const updatedNames = existingNames.slice(-3);
            return {
              ...c,
              replyCount: currentCount,
              repliesCount: currentCount,
              lastReplierNames: updatedNames,
              lastRepliers: updatedNames,
            };
          }
          return c;
        })
      );
      this.replyContent.set('');
      this.notificationService.createNotification(
        'success',
        'Reply Posted',
        'Your comment reply has been posted successfully.'
      );
    } catch (err: any) {
      console.error('[DynamicAppViewer] Failed to submit reply:', err);
      this.notificationService.createNotification(
        'error',
        'Failed to Post Reply',
        err.message || 'Could not post reply.'
      );
    } finally {
      this.isSubmittingReply.set(false);
    }
  }

  /**
   * Updates the workflow status of a task ('Open', 'In Progress', 'Ready to Test', 'Resolved', 'Closed').
   */
  public async setTaskStatus(task: UnifiedTaskItem, newStatus: string, e?: Event): Promise<void> {
    if (e) e.stopPropagation();
    if (!task || !task.rawId) return;

    // Reporter can mark as Confirmed or Reopened only when task is in Released status
    const isReporterAction =
      (newStatus === 'Confirmed' || newStatus === 'Reopened' || newStatus === 'Resolved' || newStatus === 'Reporter Rejected') &&
      this.isTaskReporter(task);

    if (newStatus === 'Confirmed' || newStatus === 'Reopened') {
      if (task.status !== 'Released') {
        this.notificationService.createNotification(
          'warning',
          'Release Required',
          'Reporter verification can only be performed after a version is released and the task is in Released status.'
        );
        return;
      }
    }

    if (!this.canApproveTasks() && !isReporterAction) {
      this.notificationService.createNotification(
        'error',
        'Permission Denied',
        'Only application owners and administrators, or the task reporter on released tasks, can change workflow status.'
      );
      return;
    }

    try {
      if (
        task.type === 'Bug' ||
        (task.rawItem as any)?.category ||
        (task.rawItem as any)?.relatedEntityName === 'App'
      ) {
        await firstValueFrom(
          this.commentController.updateById({
            id: task.rawId,
            body: { status: newStatus as any },
          })
        );
      } else {
        const appId = this.currentAppId();
        if (appId) {
          await firstValueFrom(
            this.definitionController.updateAppFeatureRequestStatus({
              id: appId,
              requestId: task.rawId,
              body: { status: newStatus as any },
            })
          );
        }
      }

      task.status = newStatus;

      this.comments.update((list) =>
        list.map((c) => {
          if (c.id === task.rawId) {
            return { ...c, status: newStatus as any };
          }
          return c;
        })
      );

      if (this.selectedTaskForDetail()?.rawId === task.rawId) {
        this.selectedTaskForDetail.update((current) =>
          current ? { ...current, status: newStatus } : null
        );
      }

      this.notificationService.createNotification(
        'success',
        'Status Updated',
        `Task status changed to "${newStatus}".`
      );
    } catch (err: any) {
      console.error('[DynamicAppViewer] Failed to update task status:', err);
      this.notificationService.createNotification(
        'error',
        'Update Failed',
        err.message || 'Could not update task status.'
      );
    }
  }

  public isNewRequestModalVisible = signal<boolean>(false);
  public isSavingReport = signal<boolean>(false);
  public savedReports = signal<any[]>([]);
  public selectedReportContent = signal<string>('');
  public isReportPreviewVisible = signal<boolean>(false);
  public isTestRunning = signal<boolean>(false);
  public virtualMouse = signal<boolean>(true);
  public testActivePageOnly = signal<boolean>(false);

  public stopE2ETests(): void {
    this.e2eRunner.stop();
    this.testLogs.update((logs) => [
      ...logs,
      {
        type: 'system',
        message: 'Test execution stopped by user.',
        success: false,
        timestamp: Date.now(),
      },
    ]);
  }

  public testLogs = signal<
    {
      type: 'system' | 'suite' | 'test';
      message?: string;
      suiteName?: string;
      stepName?: string;
      stepNumber?: number;
      explanation?: string;
      action?: string;
      success?: boolean | null;
      timestamp: number;
      error?: string;
      screenshot?: string | null;
    }[]
  >([]);
  public newRequestTargetEntity = signal<string>('');

  public openNewRequestModal(): void {
    this.newRequestTitle.set('');
    this.newRequestDescription.set('');
    this.newRequestType.set('New Feature');
    this.newRequestTargetEntity.set('');
    this.isNewRequestModalVisible.set(true);
  }

  public toggleTestMode(): void {
    const isTestMode = !this.testMode();
    this.testMode.set(isTestMode);

    if (isTestMode) {
      this.isSidebarOpen.set(true);
      this.setSidebarTab('E2E Tests');
    }
  }

  public async runE2ETests(): Promise<void> {
    if (this.isTestRunning()) return;
    this.isTestRunning.set(true);

    this.isSettingsCollapsed.set(true);
    this.testLogs.set([
      {
        type: 'system',
        message: 'Initializing E2E Test Runner Engine...',
        success: true,
        timestamp: Date.now(),
      },
      {
        type: 'system',
        message: 'Loading expectations schema from Artifacts...',
        success: true,
        timestamp: Date.now(),
      },
    ]);

    let captureVideo: HTMLVideoElement | null = null;
    let captureCanvas: HTMLCanvasElement | null = null;
    let captureStream: MediaStream | null = null;
    let mediaRecorder: MediaRecorder | null = null;
    const recordedChunks: Blob[] = [];

    this.testMode.set(true);

    if (this.captureScreenshots() || this.captureScreenshotsOnFailure() || this.recordVideo()) {
      try {
        captureStream = await navigator.mediaDevices.getDisplayMedia({
          video: { displaySurface: 'browser' },
          audio: false,
        });
        captureVideo = document.createElement('video');
        captureVideo.srcObject = captureStream;
        await captureVideo.play();
        captureCanvas = document.createElement('canvas');

        if (this.recordVideo() && typeof MediaRecorder !== 'undefined') {
          let mimeType = 'video/webm;codecs=vp9,opus';
          if (!MediaRecorder.isTypeSupported(mimeType)) mimeType = 'video/webm;codecs=vp8,opus';
          if (!MediaRecorder.isTypeSupported(mimeType)) mimeType = 'video/webm';
          if (!MediaRecorder.isTypeSupported(mimeType)) mimeType = '';
          mediaRecorder = mimeType
            ? new MediaRecorder(captureStream, { mimeType })
            : new MediaRecorder(captureStream);
          mediaRecorder.ondataavailable = (event) => {
            if (event.data && event.data.size > 0) {
              recordedChunks.push(event.data);
            }
          };
          mediaRecorder.start(1000);
        }
      } catch (e) {
        this.testLogs.update((logs) => [
          ...logs,
          {
            type: 'system',
            message:
              'Warning: Screen capture permission denied. Screenshots and video recording will be disabled.',
            success: false,
            timestamp: Date.now(),
          },
        ]);
      }
    }

    const sub = this.e2eRunner.results$.subscribe((res) => {
      let screenshotBase64 = null;
      let shouldCapture = false;
      if (res.success !== null) {
        if (this.captureScreenshots()) shouldCapture = true;
        if (this.captureScreenshotsOnFailure() && res.success === false) shouldCapture = true;
      }
      
      if (shouldCapture && captureVideo && captureCanvas && captureVideo.videoWidth > 0) {
        captureCanvas.width = captureVideo.videoWidth;
        captureCanvas.height = captureVideo.videoHeight;
        const ctx = captureCanvas.getContext('2d');
        if (ctx) {
          ctx.drawImage(
            captureVideo,
            0,
            0,
            captureCanvas.width,
            captureCanvas.height,
          );
          // Standard low quality jpeg is enough to see what happened without bloating the MD file to 50MB
          screenshotBase64 = captureCanvas.toDataURL('image/jpeg', 0.5);
        }
      }

      this.testLogs.update((logs) => {
        const idx = logs.findIndex(l => l.suiteName === res.suiteName && l.stepNumber === res.stepNumber && l.type === 'test');
        if (idx > -1) {
          const updated = [...logs];
          updated[idx] = {
            ...updated[idx],
            success: res.success,
            error: res.message || updated[idx].error,
            timestamp: Date.now(),
            screenshot: screenshotBase64 || updated[idx].screenshot
          };
          return updated;
        }
        return [
          ...logs,
          {
            type: 'test',
            suiteName: res.suiteName,
            stepName: res.stepName,
            stepNumber: res.stepNumber,
            explanation: res.explanation,
            action: res.action,
            success: res.success,
            error: res.message,
            timestamp: Date.now(),
            screenshot: screenshotBase64,
          },
        ];
      });

      // Auto-scroll timeline to active item
      setTimeout(() => {
        const activeCard = document.querySelector('#active-test-card');
        const container = document.querySelector('.e2e-timeline-container');
        if (activeCard) {
          activeCard.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } else if (container) {
          container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
        }
      }, 50);
    });

    const iframe = document.querySelector('iframe') as HTMLIFrameElement;
    if (!iframe) {
      this.testLogs.update((logs) => [
        ...logs,
        {
          type: 'system',
          message: 'Error: Cannot find iframe to execute tests against.',
          success: false,
          timestamp: Date.now(),
        },
      ]);
      if (captureStream)
        captureStream.getTracks().forEach((track) => track.stop());
      this.isTestRunning.set(false);
      return;
    }

    const manifest = this.activeManifest();
    const e2eFile =
      manifest?.e2eTests?.expectationsFile || 'e2e/expectations.json';
    let testUrl = manifest?.e2eTests?.expectationsFileUrl;

    if (!testUrl) {
      let baseUrl = this.activeAssetBaseUrl();
      if (baseUrl) {
        baseUrl = baseUrl.endsWith('/') ? baseUrl : baseUrl + '/';
      } else {
        const appId = this.currentAppId() || '';
        baseUrl = appId ? `/assets/${appId}/` : '/assets/';
      }
      testUrl = baseUrl + e2eFile;
    }

    const runTestsWithConfig = async (config: any) => {
      try {
        this.testLogs.update((logs) => [
          ...logs,
          {
            type: 'system',
            message: `Found test config for ${config.appName}`,
            success: true,
            timestamp: Date.now(),
          },
        ]);
        let suitesToRun = config.testSuites || [];
        if (this.testActivePageOnly()) {
          const currentRoute = this.cleanRoute(this.activePageRoute());
          const currentId = this.cleanRoute(this.activePageId());
          const matched = suitesToRun.filter((s: any) => {
            const sRoute = this.cleanRoute(s.route || s.suiteName);
            return (
              sRoute === currentRoute ||
              sRoute === currentId ||
              s.suiteName?.toLowerCase().includes(currentRoute)
            );
          });
          if (matched.length > 0) {
            suitesToRun = matched;
          }
        }

        for (const suite of suitesToRun) {
          if (this.e2eRunner.isStopped) {
            break;
          }
          this.testLogs.update((logs) => [
            ...logs,
            {
              type: 'suite',
              suiteName: suite.suiteName,
              message: `Running Page: ${suite.suiteName}...`,
              success: true,
              timestamp: Date.now(),
            },
          ]);
          const targetRoute = suite.route || suite.suiteName;
          const cleanTarget = this.cleanRoute(targetRoute);
          const currentRoute = this.cleanRoute(this.activePageRoute());
          const currentId = this.cleanRoute(this.activePageId());

          if (cleanTarget && currentRoute !== cleanTarget && currentId !== cleanTarget) {
            this.navigateToPage(suite.route || targetRoute);
          }

          let currentIframe: HTMLIFrameElement;
          try {
            currentIframe = await this.waitForReadyIframe(targetRoute, 15000);
          } catch (iframeErr: any) {
            this.testLogs.update((logs) => [
              ...logs,
              {
                type: 'system',
                message: `FAILED: App iframe lost after navigation to ${suite.suiteName}: ${iframeErr.message}`,
                success: false,
                timestamp: Date.now(),
              },
            ]);
            continue;
          }

          const res = await this.e2eRunner.runTestSuite(
            suite.suiteName,
            suite.tests,
            currentIframe,
            {
              virtualMouse: this.virtualMouse(),
              suiteRoute: suite.route || targetRoute,
              onNavigate: async (stepRoute: string) => {
                const cleanStepRoute = this.cleanRoute(stepRoute);
                const curRoute = this.cleanRoute(this.activePageRoute());
                const curId = this.cleanRoute(this.activePageId());
                if (
                  cleanStepRoute &&
                  curRoute !== cleanStepRoute &&
                  curId !== cleanStepRoute
                ) {
                  this.navigateToPage(stepRoute);
                  currentIframe = await this.waitForReadyIframe(stepRoute, 12000);
                  return currentIframe;
                }
                const liveIframe = (this.captureZone?.nativeElement?.querySelector('iframe') ||
                  document.querySelector('iframe')) as HTMLIFrameElement;
                if (liveIframe) {
                  currentIframe = liveIframe;
                }
                return currentIframe;
              },
            },
          );
          this.testLogs.update((logs) => [
            ...logs,
            {
              type: 'system',
              message: `Page test Finished: ${res.passed} passed, ${res.failed} failed.`,
              success: res.failed === 0,
              timestamp: Date.now(),
            },
          ]);
          if (this.e2eRunner.isStopped) break;
        }
      } catch (e: any) {
        this.testLogs.update((logs) => [
          ...logs,
          {
            type: 'system',
            message: 'Test Engine Exception: ' + e.message,
            success: false,
            timestamp: Date.now(),
          },
        ]);
      } finally {
        sub.unsubscribe();
        if (mediaRecorder && mediaRecorder.state !== 'inactive') {
          await new Promise<void>((resolve) => {
            mediaRecorder!.onstop = () => resolve();
            try {
              mediaRecorder!.stop();
            } catch {
              resolve();
            }
          });

          if (recordedChunks.length > 0) {
            const finalMime = mediaRecorder.mimeType || 'video/webm';
            const blob = new Blob(recordedChunks, { type: finalMime });
            const videoUrl = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.style.display = 'none';
            a.href = videoUrl;
            const cleanAppName = (this.appName() || 'app')
              .toLowerCase()
              .replace(/[^a-z0-9]+/g, '-');
            const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
            const filename = `${cleanAppName}-e2e-recording-${timestamp}.webm`;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            setTimeout(() => {
              document.body.removeChild(a);
              URL.revokeObjectURL(videoUrl);
            }, 1500);

            this.testLogs.update((logs) => [
              ...logs,
              {
                type: 'system',
                message: `Video recording saved: ${filename} (${(blob.size / 1024 / 1024).toFixed(2)} MB)`,
                success: true,
                timestamp: Date.now(),
              },
            ]);
          }
        }
        if (captureStream)
          captureStream.getTracks().forEach((track) => track.stop());
        this.testMode.set(false);
        this.isTestRunning.set(false);
      }
    };

    this.httpClient.get<any>(testUrl).subscribe({
      next: (config) => runTestsWithConfig(config),
      error: (e) => {
        const altUrl = testUrl.includes('/assets/')
          ? testUrl.replace('/assets/', '/')
          : testUrl.replace(/\/e2e\//, '/assets/e2e/');

        const tryLocalFallback = () => {
          const appId = this.currentAppId() || '';
          const localFallbackUrl = appId ? `/assets/${appId}/${e2eFile}` : `/assets/${e2eFile}`;
          this.testLogs.update((logs) => [
            ...logs,
            {
              type: 'system',
              message: `Primary fetch failed. Falling back to local app assets: ${localFallbackUrl}`,
              success: true,
              timestamp: Date.now(),
            },
          ]);
          this.httpClient.get<any>(localFallbackUrl).subscribe({
            next: (config) => runTestsWithConfig(config),
            error: (err) => {
              this.testLogs.update((logs) => [
                ...logs,
                {
                  type: 'system',
                  message:
                    `Failed to load ${e2eFile} from app assets.`,
                  success: false,
                  timestamp: Date.now(),
                },
              ]);
              sub.unsubscribe();
              if (captureStream)
                captureStream.getTracks().forEach((track) => track.stop());
              this.isTestRunning.set(false);
            },
          });
        };

        if (altUrl !== testUrl) {
          this.httpClient.get<any>(altUrl).subscribe({
            next: (config) => runTestsWithConfig(config),
            error: () => tryLocalFallback(),
          });
        } else {
          tryLocalFallback();
        }
      },
    });
  }

  public getTestReportMarkdown(): string {
    const logs = this.testLogs();
    if (logs.length === 0) return '';

    let mdContent = `# E2E Test Report\n\n`;
    mdContent += `**Date:** ${new Date().toLocaleString()}\n`;
    mdContent += `**App ID:** ${this.currentAppId()}\n`;
    mdContent += `**Page:** ${this.activePageId()}\n\n`;
    mdContent += `## Execution Logs\n\n`;

    for (const log of logs) {
      const time = new Date(log.timestamp).toISOString();
      if (log.type === 'system') {
        mdContent += `- [${time}] **SYSTEM**: ${log.message} ${log.success ? '✅' : '❌'}\n`;
      } else if (log.type === 'suite') {
        mdContent += `\n### Page: ${log.suiteName}\n`;
      } else {
        mdContent += `- [${time}] Step ${log.stepNumber || '-'}: ${log.stepName || log.action} - ${log.success ? 'PASS ✅' : 'FAIL ❌'}\n`;
        if (log.explanation) mdContent += `  - *${log.explanation}*\n`;
        if (log.message) mdContent += `  - **Message:** ${log.message}\n`;
        if (log.screenshot)
          mdContent += `  - <img src="${log.screenshot}" width="600" style="margin-top: 10px; border: 1px solid #ddd; border-radius: 4px;" />\n`;
      }
    }
    return mdContent;
  }

  public previewCurrentTestReport(): void {
    const mdContent = this.getTestReportMarkdown();
    if (!mdContent) return;
    this.selectedReportContent.set(mdContent);
    this.isReportPreviewVisible.set(true);
  }

  public downloadTestReport(): void {
    const mdContent = this.getTestReportMarkdown();
    if (!mdContent) return;

    const blob = new Blob([mdContent], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `e2e-report-${this.currentAppId()}-${Date.now()}.md`;
    a.click();
    URL.revokeObjectURL(url);
  }

  public async saveTestReport() {
    const mdContent = this.getTestReportMarkdown();
    if (!mdContent) return;
    this.isSavingReport.set(true);
    try {
      const fileName = `e2e-report-${this.currentAppId()}-${Date.now()}.md`;
      const res = await firstValueFrom(this.fileManagementController.getPublicUploadUrl({
        body: {
          fileName,
          contentType: 'text/markdown',
          type: 'e2e_reports',
          parentEntityId: this.currentAppId() || 'unknown',
          isProjectFile: false
        }
      }));

      // Upload to S3
      await fetch(res.uploadUrl as string, {
        method: 'PUT',
        headers: { 'Content-Type': 'text/markdown' },
        body: mdContent
      });

      // Save definition
      const isSuccess = !this.testLogs().some(l => !l.success);
      const startTime = this.testLogs()[0]?.timestamp || Date.now();
      const endTime = this.testLogs()[this.testLogs().length - 1]?.timestamp || Date.now();
      const duration = endTime - startTime;

      await firstValueFrom(this.definitionController.create({
        body: {
          name: fileName,
          key: `e2e-report-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
          parentKey: this.currentAppId() || undefined,
          type: 'E2ETestReport',
          value: {
            who: (this.authStore.user() as any)?.email || this.authStore.user()?.username || 'Anonymous',
            when: { start: startTime, end: endTime, duration },
            browser: navigator.userAgent,
            app: this.currentAppId(),
            success: isSuccess,
            s3Path: res.publicUrl
          }
        }
      }));

      this.notificationService.createNotification('success', 'Success', 'Test report saved successfully.');
      this.loadSavedReports();
    } catch (e: any) {
      console.error(e);
      this.notificationService.createNotification('error', 'Error', 'Failed to save test report.');
    } finally {
      this.isSavingReport.set(false);
    }
  }

  public async viewS3Report(report: any): Promise<void> {
    this.isReportPreviewVisible.set(true);
    this.selectedReportContent.set('Loading...');
    try {
      let reportData = report;
      if (!reportData.value && reportData.id) {
        reportData = await firstValueFrom(
          this.definitionController.findById({ id: reportData.id }),
        );
        this.savedReports.update((reports) =>
          reports.map((r) =>
            r.id === reportData.id ? { ...r, ...reportData } : r,
          ),
        );
      }
      if (!reportData?.value?.s3Path) {
        this.selectedReportContent.set('Report content or S3 path not found.');
        return;
      }
      const res = await fetch(reportData.value.s3Path);
      const text = await res.text();
      this.selectedReportContent.set(text);
    } catch (e) {
      this.selectedReportContent.set('Failed to load report content.');
    }
  }

  public async downloadSavedReport(report: any): Promise<void> {
    try {
      let reportData = report;
      if (!reportData.value && reportData.id) {
        reportData = await firstValueFrom(
          this.definitionController.findById({ id: reportData.id }),
        );
        this.savedReports.update((reports) =>
          reports.map((r) =>
            r.id === reportData.id ? { ...r, ...reportData } : r,
          ),
        );
      }
      if (reportData?.value?.s3Path) {
        window.open(reportData.value.s3Path, '_blank');
      } else {
        this.notificationService.createNotification(
          'error',
          'Error',
          'Download URL not found in report.',
        );
      }
    } catch (e) {
      this.notificationService.createNotification(
        'error',
        'Error',
        'Failed to load report download link.',
      );
    }
  }

  public loadSavedReports() {
    const appId = this.currentAppId();
    if (!appId) return;

    const filter = JSON.stringify({
      where: { parentKey: appId, type: 'E2ETestReport' },
      order: ['createdDate DESC'],
      fields: ['id', 'name', 'type', 'parentKey', 'createdDate'],
    });

    this.definitionController.find({ filter: filter as any }).subscribe({
      next: (reports) => {
        // Sort newest first
        this.savedReports.set(
          (reports || []).sort((a: any, b: any) => {
            const aTime =
              a.value?.when?.end || new Date(a.createdDate || 0).getTime();
            const bTime =
              b.value?.when?.end || new Date(b.createdDate || 0).getTime();
            return bTime - aTime;
          }),
        );
      },
      error: (e) => console.error('Failed to load saved reports', e),
    });
  }

  public closeNewRequestModal(): void {
    this.isNewRequestModalVisible.set(false);
  }

  public onTypeChange(type: string): void {
    this.newRequestType.set(type);
    this.newRequestTargetEntity.set('');
  }

  public availablePages = computed(() =>
    this.installedManifestPages().map((p) => p.route),
  );
  public availableDataModels = signal<string[]>([]); // Optional, or empty for now

  public submitFeatureRequest(): void {
    const appId = this.currentAppId();
    const title = this.newRequestTitle().trim();
    const description = this.newRequestDescription().trim();
    const type = this.newRequestType();

    if (!appId || !title || !description) return;

    this.isSubmittingFeatureRequest.set(true);
    this.definitionController
      .createAppFeatureRequest({
        id: appId,
        body: {
          type: type as any,
          title,
          description,
          targetEntity: this.newRequestTargetEntity(),
        },
      })
      .subscribe({
        next: () => {
          this.notificationService.createNotification(
            'success',
            'Request submitted successfully.',
            '',
          );
          this.newRequestTitle.set('');
          this.newRequestDescription.set('');
          this.fetchFeatureRequests();
          this.closeNewRequestModal();
          this.isSubmittingFeatureRequest.set(false);
        },
        error: (err) => {
          console.error('Failed to submit feature request:', err);
          this.notificationService.createNotification(
            'error',
            'Failed to submit request.',
            '',
          );
          this.isSubmittingFeatureRequest.set(false);
        },
      });
  }

  // AI Actions Methods
  public fetchAiActions(): void {
    const appId = this.currentAppId();
    if (!appId) return;

    // AI Actions are custom definitions with type 'AppAction' and parentKey as appId
    const filter = JSON.stringify({
      where: {
        parentKey: appId,
        type: 'AppAction',
      },
    });

    this.definitionController
      .find({ filter: filter as any })
      .subscribe({
        next: (actions) => {
          this.aiActions.set(actions || []);
        },
        error: (err) => console.error('Failed to fetch AI actions:', err),
      });
  }

  public submitAiAction(): void {
    const appId = this.currentAppId();
    const title = this.newActionTitle().trim();
    const body = this.newActionBody().trim();

    if (!appId || !title || !body) return;

    this.isSubmittingAiAction.set(true);
    this.definitionController
      .create({
        body: {
          key: `app-action-${Date.now()}`,
          parentKey: appId,
          type: 'AppAction',
          value: {
            title: title,
            actionBody: body,
          },
        } as any,
      })
      .subscribe({
        next: () => {
          this.notificationService.createNotification(
            'success',
            'AI Action created successfully.',
            '',
          );
          this.newActionTitle.set('');
          this.newActionBody.set('');
          this.fetchAiActions();
          this.isSubmittingAiAction.set(false);
        },
        error: (err) => {
          console.error('Failed to submit AI Action:', err);
          this.notificationService.createNotification(
            'error',
            'Failed to create AI Action.',
            '',
          );
          this.isSubmittingAiAction.set(false);
        },
      });
  }


  public runAiAction(action: any): void {
    const actionBody = action?.value?.actionBody;
    if (actionBody) {
      this.store.dispatch(AiChatActions.sendMessage({ text: actionBody }));
    }
  }

  public navigateToPage(pageRoute: string): void {
    const appId = this.currentAppId();
    if (appId && pageRoute) {
      this.isLoading.set(true);
      if (this.viewerMode() === 'builder') {
        this.router.navigate(['/app-builder', appId, 'preview', pageRoute]);
      } else {
        this.router.navigate(['/app', appId, pageRoute]);
      }
    }
  }

  private cleanRoute(route: string | null | undefined): string {
    return (route || '').trim().toLowerCase().replace(/^\/+|\/+$/g, '');
  }

  private async waitForReadyIframe(
    expectedRoute?: string,
    timeoutMs = 15000,
  ): Promise<HTMLIFrameElement> {
    const startTime = Date.now();
    const cleanExpected = expectedRoute ? this.cleanRoute(expectedRoute) : null;

    await new Promise((r) => setTimeout(r, 250));

    while (Date.now() - startTime < timeoutMs) {
      const currentRoute = this.cleanRoute(this.activePageRoute());
      const currentId = this.cleanRoute(this.activePageId());
      const routeMatches =
        !cleanExpected ||
        currentRoute === cleanExpected ||
        currentId === cleanExpected;

      if (!this.isLoading() && routeMatches) {
        const iframe = document.querySelector('iframe') as HTMLIFrameElement;

        if (iframe && iframe.contentWindow && iframe.contentDocument) {
          const doc = iframe.contentDocument;
          const isDocReady =
            doc.readyState === 'complete' || doc.readyState === 'interactive';
          const hasBodyContent =
            doc.body &&
            (doc.body.children.length > 0 ||
              (doc.body.innerHTML && doc.body.innerHTML.trim().length > 50));

          if (isDocReady && hasBodyContent) {
            await new Promise((r) => setTimeout(r, 350));
            return iframe;
          }
        }
      }
      await new Promise((r) => setTimeout(r, 150));
    }

    throw new Error(
      `App iframe was not ready or accessible after navigation to '${expectedRoute || 'target'}' (${timeoutMs}ms).`,
    );
  }

  public goBack(): void {
    if (this.viewerMode() === 'builder') {
      const appId = this.currentAppId();
      if (appId) {
        this.router.navigate(['/app-builder', appId]);
        return;
      }
      this.router.navigate(['/app-builder']);
    } else {
      this.router.navigate(['/applications']);
    }
  }

  public formatRelativeTime(dateStr: string | Date | undefined): string {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHours = Math.floor(diffMin / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffSec < 60) return 'Just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 30) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  }

  public formatReplierNames(names?: string[]): string {
    if (!names || !names.length) return '';
    return names.join(', ');
  }

  public formatAuthorName(name: string | undefined): string {
    if (!name) return 'Anonymous User';
    // If it looks like an email address, redact or extract name
    if (name.includes('@')) {
      const parts = name.split('@');
      const localPart = parts[0];
      // Format john.doe as John Doe
      return localPart
        .split('.')
        .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
        .join(' ');
    }
    return name;
  }

  public getInitials(name: string | undefined): string {
    if (!name) return 'U';
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  }

  public extractAvatarUrl(item: any): string | undefined {
    if (!item) return undefined;
    if (typeof item.authorAvatarUrl === 'string' && item.authorAvatarUrl.trim()) {
      return item.authorAvatarUrl.trim();
    }
    if (typeof item.authorAvatar === 'string' && item.authorAvatar.trim()) {
      return item.authorAvatar.trim();
    }
    if (typeof item.avatarUrl === 'string' && item.avatarUrl.trim()) {
      return item.avatarUrl.trim();
    }
    if (typeof item.avatar === 'string' && item.avatar.trim()) {
      return item.avatar.trim();
    }
    if (typeof item.photoUrl === 'string' && item.photoUrl.trim()) {
      return item.photoUrl.trim();
    }
    if (item.user && typeof item.user.avatarUrl === 'string' && item.user.avatarUrl.trim()) {
      return item.user.avatarUrl.trim();
    }
    if (item.user && typeof item.user.photoUrl === 'string' && item.user.photoUrl.trim()) {
      return item.user.photoUrl.trim();
    }
    if (Array.isArray(item.metadata)) {
      const found = item.metadata.find(
        (m: any) =>
          (m?.key === 'avatarUrl' || m?.key === 'avatar' || m?.key === 'photoUrl') &&
          typeof m?.value === 'string' &&
          m.value.trim()
      );
      if (found) return found.value.trim();
    } else if (item.metadata && typeof item.metadata === 'object') {
      if (typeof item.metadata.avatarUrl === 'string' && item.metadata.avatarUrl.trim()) {
        return item.metadata.avatarUrl.trim();
      }
      if (typeof item.metadata.avatar === 'string' && item.metadata.avatar.trim()) {
        return item.metadata.avatar.trim();
      }
    }
    return undefined;
  }
}
